import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './TimesheetReviewBoardPage.css';
import {
  Clock, ClipboardCheck, Shield, CheckCircle2, ArrowRight, ChevronRight,
  ChevronLeft, ChevronDown, Search, X, Inbox, Unlock, CheckCircle, AlertCircle,
  LucideIcon, Printer, FileSpreadsheet, UploadCloud, Package,
  Filter, Users, RefreshCw, Flag, Bell, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import config from '../../configLoader';
import { useSiteContext, ReviewIntent } from '../../SiteContext';
import { getPayPeriodDate } from '../../hooks/usePayPeriods';
import ConfirmModal, { ConfirmModalProps } from '../../components/ConfirmModal';
import PaidOverrideDetail from '../../components/PaidOverrideDetail';
import { isPaidNotExhaustedError, refusalMessageOf } from '../../util/timeOffUnpaid';
import EmployeeTimesheetReviewConsole, {
  bucketLabel, StatusKey, TimeOffDayEntry,
} from '../EmployeeTimesheetReviewConsole';
import { translator } from '../../i18n/localization';
import { TimesheetType } from '../../interfaces/TimesheetInterface';
import { Employee, EmployeeSickClaim } from '../../interfaces/EmployeeInterface';
import { ApproverGroup, Supervisor, SupervisorsEmployee, TimesheetSummary } from '../../interfaces/SupervisorInterface';
import {
  TimeOffRequestSummary, TimeOffRequestDetail, TimeOffRequestStatus,
} from '../../interfaces/TimeOffRequest';
import { HolidayCalendarEntry } from '../../interfaces/TimeOff';
import {
  DriveTimeEntry,
  getSupervisorsByStatus,
  getTimesheetForAnyStatus,
  getDriveTimeEntries,
  getEmployeeTimeOffForWeek,
  getEmployeeHolidays,
  approveToPayroll,
  approveSupervisorOnly,
  getSupervisorList,
  getManagerPendingApprovers,
  reassignTimesheetSupervisor,
} from '../../api/timesheetReview';
import { approveTimeOffRequest, denyTimeOffRequest, getTimeOffRequestById } from '../../api/timeOffRequests';
import { unlockTimesheet, updateSickClaimAmount, payrollApproveTimesheet } from '../../api/timesheet';
import {
  downloadSageReconciliationReport, generateSageExport, getSageExportStatus, pushSageRows, SageExportError,
  SageExportStatus, SageExportedInfo,
} from '../../api/2020ph';
import SageExportSelectModal, {
  SageExportItem, SageExportMode, fmtSageExportTime, sageExportFacts,
} from './SageExportSelectModal';
import VistaExportModal from './vistaExport/VistaExportModal';
import SpectrumExportModal from './SpectrumExportModal';
import {
  downloadSpectrumDetailFile,
  downloadSpectrumSummaryFile,
  driftTooltip,
  formatDriftEdit,
  formatDriftTotalsShift,
  getSpectrumChanges,
  getSpectrumExportStatus,
  sortedDriftEdits,
  spectrumExportFacts,
  SpectrumDriftEmployee,
  SpectrumExportedInfo,
  SpectrumExportStatus,
  SpectrumPushResult,
} from '../../api/wallcraftSpectrum';
import WcdpIasExportModal from './WcdpIasExportModal';
import {
  downloadWcdpPaycorFile, getWcdpIasExportStatus, getWcdpPaycorPreview, WcdpExportStatus,
} from '../../api/wcdpIasExport';
import {
  plural, wcdpChipKind, wcdpChipText, wcdpCrewExportCount, WcdpExportMap, wcdpPaycorConfirmModel,
} from './wcdpExportModel';
import FloatMenuButton from '../../components/v2/FloatMenuButton';
import { supervisorOptionLabel, sortSupervisorOptions, supervisorNumbersOn } from '../../utils/supervisorPickerOptions';
import { downloadPayrollApprovedSummaryReport } from '../../api/payrollApprovedSummaryReport';
import { downloadVistaPostVerificationReport } from '../../api/vistaPostVerificationReport';
import { JobStatus } from '../../interfaces/V2Interface';
import { printTimesheetBatch, printTimesheetBatchAll } from '../../util/PrintUtil';
import { can, canApproveStage, canUnlockStage, canEditEntries, canSubmitForEmployee } from '../../util/adminPermissions';
import { sendUnsubmittedNotification } from '../../api/notifications';
import { formatHours, formatDecimalHours } from '../../util/hoursFormat';
import TimesheetEditManager from '../../managers/TimesheetEditManager';
import {
  PayrollTimesheetStateEditable,
  TimesheetPreviewEditable,
} from '../../interfaces/TimesheetEditInterface';
import InactiveBadge from '../../components/payrollEdit/InactiveBadge';
import PayrollSubmitWizard from '../../components/payrollEdit/PayrollSubmitWizard';
import { Page } from '../../interfaces/PagesInterface';
import {
  boardSuperSlotChip, canApproveModifiedDutyOnBehalf, isWaitingOnAnotherApprover, waitingApprovalText,
} from '../../util/modifiedDutyApprovals';

// ── Constants ──────────────────────────────────────────────────────────────

const STATUS_KEYS: StatusKey[] = ['unsubmitted', 'managerPending', 'submitted', 'supervisorApproved', 'payrollApproved'];

// Payroll Approved "Sage export" display filter (TAS-180), remembered per viewer. Storage
// can throw (private window, blocked site data) — the board just falls back to 'all'.
type SageExportFilter = 'all' | 'notExported' | 'exported';
const SAGE_FILTER_KEY = 'trb.sageExportFilter';
const SAGE_STATUS_LOADING_TITLE = 'Loading this week’s Sage export history…';
function readSageFilter(): SageExportFilter {
  try {
    const v = localStorage.getItem(SAGE_FILTER_KEY);
    return v === 'notExported' || v === 'exported' ? v : 'all';
  } catch {
    return 'all';
  }
}
function writeSageFilter(v: SageExportFilter): void {
  try { localStorage.setItem(SAGE_FILTER_KEY, v); } catch { /* storage unavailable — ignore */ }
}
const SAGE_CHANGE_REASON_TEXT: Record<string, string> = {
  reapproved: 'unlocked and re-approved after export',
  notApproved: 'no longer payroll approved',
};

type PillVariant = 'neutral' | 'warn' | 'info' | 'ok' | 'teal' | 'purple';

interface BucketMeta {
  k: StatusKey;
  label: string;
  icon: LucideIcon;
  tone: 'amber' | 'teal' | 'blue' | 'purple' | 'green';
  pill: PillVariant;
  sub: string;
  approve?: string; // primary approve verb (queue bar)
  /** What one group row IS in this stage — drives the filter-bar label, the stage
   *  sub-label count and the crew button. Every shipped stage groups by supervisor;
   *  only the 1st Approval stage groups by approver (manager or approval group). */
  groupWord: 'supervisor' | 'approver' | 'required super';
}

// Labels come from the shared translator map, so NPM reads "1st / 2nd Approval
// Pending" and every other tenant reads the defaults. configLoader (imported above)
// has already run translator.setCompany by the time this module body evaluates.
const ALL_BUCKETS: BucketMeta[] = [
  { k: 'unsubmitted', label: bucketLabel('unsubmitted'), icon: Clock, tone: 'amber', pill: 'neutral', sub: 'awaiting employee submit', groupWord: 'supervisor' },
  { k: 'managerPending', label: bucketLabel('managerPending'), icon: ClipboardCheck, tone: 'teal', pill: 'teal', sub: 'needs manager review', groupWord: 'approver' },
  { k: 'submitted', label: bucketLabel('submitted'), icon: ClipboardCheck, tone: 'blue', pill: 'warn', sub: 'needs supervisor review', approve: 'Approve Week', groupWord: config.useRequiredSuperApproval === true ? 'required super' : 'supervisor' },
  { k: 'supervisorApproved', label: bucketLabel('supervisorApproved'), icon: Shield, tone: 'purple', pill: 'purple', sub: 'awaiting payroll', approve: 'Payroll Approve', groupWord: 'supervisor' },
  { k: 'payrollApproved', label: bucketLabel('payrollApproved'), icon: CheckCircle2, tone: 'green', pill: 'ok', sub: 'finalized this week', groupWord: 'supervisor' },
];
// NPM's two-level approval: managers / approval groups sign first, then the required
// super. Every other tenant renders the shipped four stages unchanged.
const managerApprovalOn = config.allowManagerApproval === true;
const BUCKETS: BucketMeta[] = ALL_BUCKETS.filter((b) => b.k !== 'managerPending' || managerApprovalOn);
// Keyed over ALL stages (not just the rendered ones) so a lookup by a stage the
// tenant does not show still answers instead of crashing.
const BMAP: Record<StatusKey, BucketMeta> = Object.fromEntries(ALL_BUCKETS.map((b) => [b.k, b])) as Record<StatusKey, BucketMeta>;

// Tenants with useSingleHopPayrollApproval jump a Submitted timesheet straight to Payroll
// Approved; everyone else supervisor-approves first and payroll-approves as a second step.
const singleHop = config.useSingleHopPayrollApproval === true;

// Payroll Timesheet Modifications (M1-B3): submit-for-employee + payroll editing
// of unsubmitted weeks. Every surface below is additive — flag off renders the
// board and drill-down byte-identical.
const payrollEditingOn = config.payrollTimesheetEditing?.enabled === true;

// Bulk payroll approve ("Approve All", ported from the v1 Payroll Pending employee list).
// There is no bulk approve endpoint by design, so this is a client-side loop — v1 ran it
// strictly serially, which is minutes of wall clock on a real bucket. A small fixed
// concurrency keeps it responsive without hammering the DB: every /payroll/approve runs a
// transaction plus a full snapshot re-stamp that re-reads the whole timesheet, and this
// database has a documented history of DTU pinning under parallel timesheet reads.
const BULK_APPROVE_CONCURRENCY = 3;

// The server's WP6 block, raised as a HTTP-200 { success:false, errorMessage }. A row that
// trips it was blocked for the same reason the pre-screen blocks rows, so it is reported as
// SKIPPED rather than failed.
const SICK_RATE_BLOCK_RE = /sick hourly rate/i;

// The bucket that a timesheet moves to after "Approve Week".
const NEXT_BUCKET: Record<StatusKey, StatusKey | null> = {
  unsubmitted: null,
  // The last outstanding first-level slot moves the timesheet to the submitted
  // (2nd Approval Pending) stage — never straight to payroll.
  managerPending: 'submitted',
  submitted: singleHop ? 'payrollApproved' : 'supervisorApproved',
  supervisorApproved: 'payrollApproved',
  payrollApproved: null,
};

// ── Types ──────────────────────────────────────────────────────────────────

interface QueueEntry {
  employeeID: number;
  altEmployeeID: string;
  name: { first: string; middle?: string; last: string };
  supervisorName: string;
  /** The crew group this entry came from. Change Supervisor needs the ID, not just
   *  the name — neither the timesheet nor the board row carries a supervisorID.
   *  0 when the entry was synthesized outside a group (the global-search fallback). */
  supervisorID: number;
}

type View = 'board' | 'queue';

// ── Helpers ────────────────────────────────────────────────────────────────

function isoDateOnly(d: Date): string {
  return d.toISOString().split('T')[0];
}

function initials(first: string, last: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

function displayName(n: { first: string; last: string }): string {
  return `${n.first} ${n.last}`.trim();
}

/** How a group row is named in the list, the filter options and the queue scope.
 *  An approval group has no person behind it, so it reads as its group name. */
function groupLabel(g: ApproverGroup): string {
  return g.kind === 'approvalGroup' ? g.name.last : `${g.name.last}, ${g.name.first}`;
}

/** Avatar initials for a group row — an approval group has no first/last split, so
 *  the initials of its first two words stand in. */
function groupInitials(g: ApproverGroup): string {
  if (g.kind !== 'approvalGroup') return initials(g.name.first, g.name.last);
  const words = g.name.last.trim().split(/\s+/);
  return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase() || '—';
}

/** "Jun 15 – 21, 2026"-style range for a pay period (collapses a shared month). */
function formatPayPeriodRange(pp: { StartDate: string | Date; EndDate: string | Date }): string {
  const s = new Date(pp.StartDate);
  const e = new Date(pp.EndDate);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const start = s.toLocaleDateString('en-US', opts);
  const end = s.getUTCMonth() === e.getUTCMonth()
    ? String(e.getUTCDate())
    : e.toLocaleDateString('en-US', opts);
  return `${start} – ${end}, ${e.getUTCFullYear()}`;
}

// ── Summary strip (TAS-263/264) ────────────────────────────────────────────
// Snapshot minutes → hours, and claimed dollars, both in the design's exact format.

/** Snapshot minutes rendered in the tenant's hours format (TAS-75) — decimal for
 *  2020PH, HH:MM for WCDP/NPM. The strings stay the same width class, so the
 *  tabular-nums cell metrics are unchanged. Dollars below are NOT affected. */
function fmtHrs(minutes: number): string {
  return formatHours(minutes);
}

function fmtMoney(amount: number): string {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A snapshot answers a question only when it exists AND was stamped by the current
 *  rule set. Everything else dashes — a missing summary is never coalesced to zeros. */
function isUsable(e: SupervisorsEmployee): boolean {
  return !!e.summary && !e.snapshotStale;
}

/** Does this row have a sick claim with minutes but no rate — the condition payroll approval
 *  hard-blocks on? The API computes it LIVE for the Payroll Pending bucket and sends it
 *  top-level; the snapshot copy is the fallback for other buckets. Prefer the live value:
 *  supervisor approval never stamps a snapshot, so a Payroll Pending row usually has no
 *  summary at all and the stored flag would read as "fine" for a timesheet the approve
 *  endpoint will reject. No tenant gating here — this is the raw fact; callers apply their
 *  own gate (cap.sickRate for display, hideSickClaimHourlyRate for the approve pre-screen). */
function needsSickRate(e: SupervisorsEmployee): boolean {
  return e.needsSickHourlyRate ?? e.summary?.needsSickHourlyRate ?? false;
}

/** Stable hashed hue for employee avatars. */
function hashHue(s: string): number {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) & 0x7fffffff;
  return h % 360;
}

// ── Flag facet (design §3, Q9) ─────────────────────────────────────────────
// The employee row is a single <button>, so its chips cannot be interactive. The
// same chip vocabulary becomes a facet in the filter bar instead: click a flag to
// narrow the list to the employees carrying it. Multi-select is ANY-of, and it
// composes with — never replaces — Location / Supervisor / text.

/** The capability keys a facet can be gated on (`null` = every tenant has it). */
type FlagCapKey = 'sickRate' | 'meal' | 'trueup' | 'supp' | 'pw' | 'holiday' | 'misc' | 'cs' | 'md';

interface FlagDef {
  k: string;
  cls: string;
  label: string;
  cap: FlagCapKey | null;
  test: (s: TimesheetSummary) => boolean;
  /** Row-level test for a facet whose signal does NOT come from the snapshot. A facet with
   *  one can judge a row that has no summary at all, so it is never held out. */
  rowTest?: (e: SupervisorsEmployee) => boolean;
}

/** Facet order is by decision value, not alphabetical (design §3). */
const FLAG_DEFS: FlagDef[] = [
  { k: 'sickrate', cls: 'blockf', label: 'Sick rate',    cap: 'sickRate', test: (s) => s.needsSickHourlyRate, rowTest: needsSickRate },
  { k: 'meal',     cls: 'meal',   label: 'Meal penalty', cap: 'meal',     test: (s) => s.mealPenaltyDayCount > 0 },
  { k: 'ot',       cls: 'otf',    label: 'OT / DT',      cap: null,       test: (s) => s.overtimeMinutes > 0 || s.doubleTimeMinutes > 0 },
  { k: 'misc',     cls: 'misc',   label: 'Misc tasks',   cap: 'misc',     test: (s) => s.hasMiscTasks },
  // cls is "csw", not "cs" — short generic class names collide across page stylesheets
  // (the same reason `pw` is `pwflag` below).
  { k: 'cs',       cls: 'csw',    label: 'Cust. service', cap: 'cs',      test: (s) => s.hasCustomerService },
  { k: 'trueup',   cls: 'trueup', label: 'True-up',      cap: 'trueup',   test: (s) => s.hasMinWageTrueUp },
  { k: 'supp',     cls: 'supp',   label: 'Supp pay',     cap: 'supp',     test: (s) => s.hasSupplementalPay },
  // cls is "pwflag", not "pw" — ProjectWorkPage.css owns the bare .pw class as its page scope.
  { k: 'pw',       cls: 'pwflag', label: 'PW',           cap: 'pw',       test: (s) => s.hasPrevailingWage },
  { k: 'hol',      cls: 'hol',    label: 'Holiday',      cap: 'holiday',  test: (s) => s.hasHoliday },
  { k: 'pto',      cls: 'pto',    label: 'Time off',     cap: null,       test: (s) => s.hasTimeOff },
  { k: 'sick',     cls: 'sick',   label: 'Sick time',    cap: null,       test: (s) => s.hasSickTime },
  // TAS-679 (WCDP): row-level — `hasModifiedDuty` is computed at read time, not stamped in
  // the snapshot. cap.md is config.showModifiedDuty, so no other tenant gets the facet.
  // cls is "mdflag", not "md" — ModifiedDutyPage.css owns the md-* namespace.
  { k: 'md',       cls: 'mdflag', label: 'Modified Duty', cap: 'md',      test: () => false, rowTest: (e) => !!e.hasModifiedDuty },
];

function buildTimeOffDetailMap(details: TimeOffRequestDetail[]): Record<string, TimeOffDayEntry[]> {
  const map: Record<string, TimeOffDayEntry[]> = {};
  details.forEach((req) => {
    (req.details ?? []).forEach((d) => {
      const key = d.requestDate.split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push({
        requestID: req.timeOffRequestID,
        category: req.categoryName,
        hours: d.hours,
        status: req.status,
        // Unpaid Time (TAS-586) — per request, so every day of it carries the flags.
        isUnpaid: req.isUnpaid,
        countsForHolidayEligibility: req.countsForHolidayEligibility,
      });
    });
  });
  return map;
}

// ── Component ────────────────────────────────────────────────────────────────

const TimesheetReviewBoardPage: React.FC = () => {
  const {
    userID, userName, userRole, payPeriods, payPeriodID, week, locations,
    reviewIntent, setReviewIntent, setPage, setPayrollEditIntent,
    setWeek, setYear, setPayPeriodID, setStartDate, setEndDate,
  } = useSiteContext();

  // ── Week (derived from context payPeriodID — never from WeekNumber) ─────────
  const reviewWeekIdx = Math.max(0, payPeriods.findIndex((pp) => pp.PayPeriodID === payPeriodID));
  const currentPayPeriod = payPeriods[reviewWeekIdx];
  const currentPayPeriodID = currentPayPeriod?.PayPeriodID ?? payPeriodID;

  // ── View / board state ──────────────────────────────────────────────────────
  const [view, setView] = useState<View>('board');
  // Groups per stage. Every stage but managerPending is grouped by supervisor; that one
  // is grouped by APPROVER, normalized onto the same shape (see ApproverGroup).
  const [allSupervisorData, setAllSupervisorData] = useState<Record<StatusKey, ApproverGroup[]>>({
    unsubmitted: [], managerPending: [], submitted: [], supervisorApproved: [], payrollApproved: [],
  });
  // The server's DISTINCT timesheet count for the 1st Approval stage (a timesheet is
  // listed under every approver still owing a signature). Optional on the payload, and
  // pay-period-wide — the board falls back to its own dedupe whenever a filter narrows
  // the set. null = not provided.
  const [managerPendingDeduped, setManagerPendingDeduped] = useState<number | null>(null);
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [bucketsLoaded, setBucketsLoaded] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<StatusKey>('unsubmitted');
  const [locationFilter, setLocationFilter] = useState<number | 'all'>('all');
  const [supervisorFilter, setSupervisorFilter] = useState<number | 'all'>('all');
  const [textFilter, setTextFilter] = useState('');
  // Flag facet (TAS-264 / design Q9) — the selected flag keys, ANY-of.
  const [flagFilter, setFlagFilter] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // ── Queue state ─────────────────────────────────────────────────────────────
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [qi, setQi] = useState(0);
  const [origin, setOrigin] = useState<{ payPeriodID: number; bucket: StatusKey; scope: string } | null>(null);
  const [approvedIDs, setApprovedIDs] = useState<Set<number>>(new Set());

  // ── Review state (reused from TR2) ──────────────────────────────────────────
  const [timesheetData, setTimesheetData] = useState<Employee | null>(null);
  const [driveTimeEntries, setDriveTimeEntries] = useState<DriveTimeEntry[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequestSummary[]>([]);
  const [timeOffDetailMap, setTimeOffDetailMap] = useState<Record<string, TimeOffDayEntry[]>>({});
  const [holidays, setHolidays] = useState<HolidayCalendarEntry[]>([]);
  const [actualStatus, setActualStatus] = useState<StatusKey | null>(null);
  const [noTimesheetForWeek, setNoTimesheetForWeek] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // ── Payroll Timesheet Modifications (M1-B3) ─────────────────────────────────
  // payrollState: the raw timesheetEdit/state Shape B when the drill-down loaded
  // an unsubmitted week through it (entryCounts / questions / day collections —
  // the submit wizard's inputs). payrollPreview: the timesheetEdit/preview week
  // (closed decision 1: the drill-down's Regular/OT/DT numbers are the server's).
  const [payrollState, setPayrollState] = useState<PayrollTimesheetStateEditable | null>(null);
  const [payrollPreview, setPayrollPreview] = useState<TimesheetPreviewEditable | null>(null);
  const [payrollPreviewLoading, setPayrollPreviewLoading] = useState(false);
  const [submitWizardOpen, setSubmitWizardOpen] = useState(false);

  // ── Approve / sick-rate modal ───────────────────────────────────────────────
  const [approveLoading, setApproveLoading] = useState(false);
  // Bulk payroll approve. `bulkCrewId` is the crew whose header button started the run
  // (null = the bucket-level bar), so only that one button shows the running label.
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkCrewId, setBulkCrewId] = useState<number | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const bulkAbortRef = useRef<AbortController | null>(null);
  const [sickRateModalOpen, setSickRateModalOpen] = useState(false);
  const [sickRateInput, setSickRateInput] = useState('');

  // ── Sick-rate panel (payroll-pending console) ───────────────────────────────
  // Standalone rate entry so payroll can see and save the sick hourly rate without going
  // through Approve. The input mirrors an already-SAVED rate; it is never prefilled with
  // the base rate (D8).
  const [sickPanelRate, setSickPanelRate] = useState('');
  const [sickRateSaving, setSickRateSaving] = useState(false);

  // ── Change Supervisor (per-timesheet reassignment) ──────────────────────────
  // Ported from v1's TimesheetPage button. Moves ONE timesheet to another supervisor
  // for this pay period only — the employee's standing hierarchy is untouched.
  const [supModalOpen, setSupModalOpen] = useState(false);
  const [supOptions, setSupOptions] = useState<
    { employeeID: number; name: { first: string; middle: string; last: string }; altEmployeeID?: string }[]
  >([]);
  const [supOptionsLoading, setSupOptionsLoading] = useState(false);
  const [supPick, setSupPick] = useState<number | null>(null);
  const [supSaving, setSupSaving] = useState(false);

  // ── Confirm / result modal ──────────────────────────────────────────────────
  type CMConfig = Omit<ConfirmModalProps, 'onClose'> & { onDismiss?: () => void };
  const [cmConfig, setCMConfig] = useState<CMConfig | null>(null);
  const dismissCM = () => {
    if (cmConfig?.onDismiss) cmConfig.onDismiss();
    setCMConfig(null);
  };
  const showError = (title: string, subtitle?: string) =>
    setCMConfig({ variant: 'error', icon: <AlertCircle size={22} />, title, subtitle });

  // Sage 422s come in two flavours, both of which abort the whole export and name every row
  // involved: `issues` is our own pre-flight hour reconciliation, `rejections` is Sage itself
  // refusing rows on a push. Render either list so payroll knows exactly what is blocking them.
  const showSageBlocked = (error: unknown, fallback: string) => {
    const sageErr = error instanceof SageExportError ? error : null;
    const nameFor = (emp: { employeeID: number; altEmployeeID: string }) => {
      const match = sageItems.find((s) => s.employeeID === emp.employeeID);
      if (match) return `${match.name.last}, ${match.name.first}`;
      // employeeID 0 means the API could not map Sage's line number back to a row.
      return emp.altEmployeeID ? `#${emp.altEmployeeID}` : 'Unmatched row';
    };

    // 409 — the selection holds employees already exported this pay period (a stale status
    // map, or another clerk got there first). Nothing was sent.
    if (sageErr?.alreadyExported?.length) {
      const list = sageErr.alreadyExported;
      setCMConfig({
        variant: 'warning',
        icon: <AlertTriangle size={22} />,
        title: 'Already exported',
        subtitle: `${list.length} employee${list.length === 1 ? ' was' : 's were'} already exported to Sage this pay period — nothing was sent. Re-open Export to Sage and confirm "Export again" if they really must go out twice.`,
        detail: (
          <div className="tr-bulkgroup">
            <div className="tr-bulkgrouphd">Already in Sage</div>
            <ul className="tr-bulklist">
              {list.map((emp) => (
                <li key={`${emp.employeeID}-${emp.altEmployeeID}`}>
                  <b>{emp.name || nameFor(emp)}</b>
                  {emp.altEmployeeID ? ` #${emp.altEmployeeID}` : ''} — exported {fmtSageExportTime(emp.exportedAt)}
                  {emp.exportedBy ? ` by ${emp.exportedBy}` : ''} · {emp.rowCount} row{emp.rowCount === 1 ? '' : 's'} ·{' '}
                  {Number(emp.hours ?? 0).toFixed(2)} hrs
                  {emp.confirmed === false ? ' · unconfirmed — check Sage' : ''}
                </li>
              ))}
            </ul>
            <div className="tr-sagewarn">
              <AlertTriangle size={15} /> To redo an export, delete the earlier lines in Sage Daily Payroll if that
              batch is still open, otherwise correct with a Sage adjustment.
            </div>
          </div>
        ),
      });
      return;
    }

    // 502 — Sage's answer never arrived. The rows MAY be in Sage; the API recorded them as
    // unconfirmed, so a plain re-export is now blocked until someone checks Sage.
    if (sageErr?.unconfirmed) {
      setCMConfig({
        variant: 'warning',
        icon: <AlertTriangle size={22} />,
        title: 'Sage did not answer — check Sage before re-exporting',
        subtitle: 'The rows MAY already be in Sage. They are recorded as unconfirmed, so these employees now show “Unconfirmed – check Sage”.',
        detail: (
          <div className="tr-sagewarn strong">
            <AlertTriangle size={16} />
            <span>
              {sageErr.message}
              {' '}Run <b>Reconcile with Sage</b> or check Sage Daily Payroll before exporting these employees again.
            </span>
          </div>
        ),
      });
      return;
    }

    if (sageErr?.rejections?.length) {
      const total = sageErr.rejections.reduce((n, e) => n + e.rejections.length, 0);
      setCMConfig({
        variant: 'error',
        icon: <AlertCircle size={22} />,
        title: 'Sage rejected the push',
        subtitle: `Sage refused ${total} row${total === 1 ? '' : 's'} across ${sageErr.rejections.length} employee${sageErr.rejections.length === 1 ? '' : 's'} — nothing was saved to Sage.`,
        detail: (
          <div className="tr-bulkgroup">
            <div className="tr-bulkgrouphd">Rows Sage refused</div>
            <ul className="tr-bulklist">
              {sageErr.rejections.flatMap((emp) =>
                emp.rejections.map((rej, i) => (
                  <li key={`${emp.employeeID}-${emp.altEmployeeID}-${i}`}>
                    <b>{nameFor(emp)}</b> — {rej.workDate || 'no date'} · {rej.code}
                    {rej.value ? `: ${rej.value}` : ''}
                    {rej.column ? ` (${rej.column})` : ''}
                  </li>
                )))}
            </ul>
          </div>
        ),
      });
      return;
    }

    if (sageErr?.issues?.length) {
      const total = sageErr.issues.reduce((n, e) => n + e.issues.length, 0);
      setCMConfig({
        variant: 'error',
        icon: <AlertCircle size={22} />,
        title: 'Sage export blocked',
        subtitle: `${total} hour-reconciliation issue${total === 1 ? '' : 's'} across ${sageErr.issues.length} employee${sageErr.issues.length === 1 ? '' : 's'} — nothing was exported.`,
        detail: (
          <div className="tr-bulkgroup">
            <div className="tr-bulkgrouphd">Unreconciled hours</div>
            <ul className="tr-bulklist">
              {sageErr.issues.flatMap((emp) =>
                emp.issues.map((iss, i) => (
                  <li key={`${emp.employeeID}-${i}`}>
                    <b>{nameFor(emp)}</b> — {iss.workDate || 'week'}
                    {iss.payCodeName ? ` · ${iss.payCodeName}` : ''}{' '}
                    {iss.deltaHours > 0 ? '+' : ''}{iss.deltaHours.toFixed(2)}h ({iss.kind})
                  </li>
                )))}
            </ul>
          </div>
        ),
      });
      return;
    }
    const msg = sageErr?.message ?? (error instanceof Error ? error.message : '');
    if (msg && !/^Request failed with status code/.test(msg)) {
      setCMConfig({
        variant: 'error',
        icon: <AlertCircle size={22} />,
        title: 'Sage export failed',
        detail: <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{msg}</pre>,
      });
      return;
    }
    showError('Sage export failed', fallback);
  };

  // ── Print / Sage export (Payroll Approved bucket only) ──────────────────────
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  };
  const [sageExcelLoading, setSageExcelLoading] = useState(false);
  const [sagePushLoading, setSagePushLoading] = useState(false);
  const [sageReconLoading, setSageReconLoading] = useState(false);
  const [sageReconJob, setSageReconJob] = useState<JobStatus | null>(null);
  // Runs for minutes like the Vista jobs — abort the poller on unmount.
  const sageReconAbortRef = useRef<AbortController | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryJob, setSummaryJob] = useState<JobStatus | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyJob, setVerifyJob] = useState<JobStatus | null>(null);
  // This job runs for minutes; abort the poller on unmount so it does not outlive the page.
  const verifyAbortRef = useRef<AbortController | null>(null);
  const [printingSupId, setPrintingSupId] = useState<number | null>(null);
  const [printAllLoading, setPrintAllLoading] = useState(false);
  const [printAllJob, setPrintAllJob] = useState<JobStatus | null>(null);
  const printAllAbortRef = useRef<AbortController | null>(null);
  // NPM Vista export — the crew whose modal is open, or null. The export is per-supervisor
  // at the API (`supervisorID` is required), so the crew card is the only entry point.
  const [vistaCrew, setVistaCrew] = useState<ApproverGroup | null>(null);
  // Wallcraft Spectrum export — same per-supervisor shape as the Vista modal above
  // (the API's /admin/wallcraft/spectrum endpoints all require a supervisorID), so the
  // crew header is the only entry point. Replaces the retired Pick-Up Sheet download.
  const [spectrumCrew, setSpectrumCrew] = useState<Supervisor | null>(null);
  // Wallcraft: employees whose live timecard no longer matches their latest Spectrum push
  // (GET /admin/wallcraft/spectrum/changes). Drives the amber row/crew-header chips; bump
  // driftNonce to refetch outside the automatic triggers (e.g. right after a push).
  const [spectrumDrift, setSpectrumDrift] = useState<SpectrumDriftEmployee[]>([]);
  const [driftNonce, setDriftNonce] = useState(0);
  // Wallcraft: who has been pushed to Spectrum this week (GET /admin/wallcraft/spectrum/status).
  // Same role as sageStatus below — null = not loaded (no chips, no filter). Refetched on driftNonce.
  const [spectrumStatus, setSpectrumStatus] = useState<SpectrumExportStatus | null>(null);
  // 2020PH/NV: the Sage export record for the week (GET /admin/2020ph/sageExportStatus).
  // null = not loaded / unavailable (no chips, no filter). Bump sageStatusNonce after any
  // push attempt — success or failure — since a failed push can still record lines.
  const [sageStatus, setSageStatus] = useState<SageExportStatus | null>(null);
  const [sageStatusNonce, setSageStatusNonce] = useState(0);
  // The pay period whose export record has SETTLED (loaded or failed). Until it matches the
  // current week both Sage export buttons stay disabled: opening the picker before the record
  // arrives starts every already-exported employee checked (10/20 — one click from re-sending 186).
  const [sageStatusSettledPP, setSageStatusSettledPP] = useState<number | null>(null);
  const [sageFilter, setSageFilterState] = useState<SageExportFilter>(readSageFilter);
  const setSageFilter = (v: SageExportFilter) => { setSageFilterState(v); writeSageFilter(v); };
  // Wallcraft Weekly Summary / Payroll Detail (as pushed to Spectrum) — the drift check +
  // the download. Holds which of the two is in flight (null = idle).
  const [wcDownloading, setWcDownloading] = useState<null | 'summary' | 'detail'>(null);
  // WCDP IAS export (TAS-682): the crew whose export modal is open, the pay period's export
  // record (null = not loaded — no chips, no counts), a nonce bumped after every export /
  // Paycor attempt, and which step of "Generate Paycor File" is in flight.
  const [wcdpCrew, setWcdpCrew] = useState<Supervisor | null>(null);
  const [wcdpStatus, setWcdpStatus] = useState<WcdpExportStatus | null>(null);
  const [wcdpStatusNonce, setWcdpStatusNonce] = useState(0);
  const [paycorBusy, setPaycorBusy] = useState<null | 'preview' | 'file'>(null);

  // Pay-period-wide payroll-approved count — used by the Payroll Approved Summary
  // toast (that report always covers the whole pay period).
  const payrollApprovedTotal = (allSupervisorData.payrollApproved ?? [])
    .reduce((s, g) => s + (g.employees?.length ?? g.count ?? 0), 0);

  // ── Remind all unsubmitted (period-wide push notification) ──────────────────
  // Replaces the capability the retired v1 Supervisor List carried. The endpoint is
  // pay-period-wide — there is no per-employee reminder — so the count below is the
  // WHOLE unsubmitted bucket, deliberately ignoring the list filters.
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);
  const unsubmittedTotal = (allSupervisorData.unsubmitted ?? [])
    .reduce((s, g) => s + (g.employees?.length ?? g.count ?? 0), 0);
  // Company config (showPushNotificationButton) + the SendTimesheetReminders role
  // permission (super admin implies it). Same gate as v1's SupervisorListPage.
  const canRemindUnsubmitted = config.showPushNotificationButton === true && can(userRole, 'sendTimesheetReminders');

  // A new week is a new reminder.
  useEffect(() => { setReminderSent(false); }, [currentPayPeriodID]);

  const doRemindUnsubmitted = async () => {
    setReminderSending(true);
    try {
      const result = await sendUnsubmittedNotification(currentPayPeriodID);
      if (result.success) {
        setReminderSent(true);
        const nr = result.notificationResult;
        setCMConfig({
          variant: 'success',
          icon: <CheckCircle size={22} />,
          title: 'Reminder sent',
          subtitle: `${result.employeeCount ?? unsubmittedTotal} unsubmitted employee${(result.employeeCount ?? unsubmittedTotal) === 1 ? '' : 's'} · Week ${week}`,
          detail: nr
            ? <div>{nr.totalSuccessCount} notification{nr.totalSuccessCount === 1 ? '' : 's'} delivered{nr.totalFailureCount > 0 ? `, ${nr.totalFailureCount} failed` : ''}.</div>
            : <div>{result.message}</div>,
        });
      } else {
        showError('Reminder not sent', result.message || 'Could not send the push reminder. Please try again.');
      }
    } finally {
      setReminderSending(false);
    }
  };

  const handleRemindUnsubmitted = () => {
    setCMConfig({
      variant: 'warning',
      icon: <Bell size={22} />,
      title: `Send a push reminder to all ${unsubmittedTotal} unsubmitted employee${unsubmittedTotal === 1 ? '' : 's'}?`,
      subtitle: `Week ${week}`,
      detail: (
        <div>
          Every employee with an unsubmitted timesheet this pay period gets one push
          notification. The reminder covers the whole pay period — the filters on this
          list do not narrow it, and it cannot be recalled.
        </div>
      ),
      confirmLabel: 'Send reminder',
      onConfirm: () => { setCMConfig(null); void doRemindUnsubmitted(); },
    });
  };

  // Sage export selection (design_handoff_sage_export_selection): both bar buttons
  // open the shared employee picker; the export sends only the checked IDs.
  const [sageModalMode, setSageModalMode] = useState<SageExportMode | null>(null);

  const handleSageExcel = () => {
    if (!currentPayPeriod || sageExcelLoading || sagePushLoading) return;
    setSageModalMode('excel');
  };

  const handleSageExcelConfirm = async (ids: number[]) => {
    if (!currentPayPeriod || sageExcelLoading) return;
    setSageExcelLoading(true);
    const fileName = `SagePayroll-${currentPayPeriod.AccountingYear}-Week${currentPayPeriod.WeekNumber}.xlsx`;
    try {
      await generateSageExport(currentPayPeriodID, fileName, ids);
      setSageModalMode(null);
      showToast(`Sage Export Excel ready — ${ids.length} timesheet${ids.length === 1 ? '' : 's'}, Week ${week}.`);
    } catch (error) {
      setSageModalMode(null);
      showSageBlocked(error, 'Could not generate the Sage Excel file. Please try again.');
    } finally {
      setSageExcelLoading(false);
    }
  };

  const handlePayrollSummary = async () => {
    if (!currentPayPeriod || summaryLoading) return;
    setSummaryLoading(true);
    setSummaryJob(null);
    const fileName = `PayrollApprovedSummary_Week${currentPayPeriod.WeekNumber}_${currentPayPeriod.AccountingYear}.xlsx`;
    try {
      await downloadPayrollApprovedSummaryReport(currentPayPeriodID, fileName, setSummaryJob);
      showToast(`Payroll Approved Summary ready — ${payrollApprovedTotal} approved timesheet${payrollApprovedTotal === 1 ? '' : 's'}, Week ${week}.`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Could not generate the Payroll Approved Summary report.';
      showError('Payroll summary failed', msg);
    } finally {
      setSummaryLoading(false);
      setSummaryJob(null);
    }
  };

  const handleVistaPostVerification = async () => {
    if (!currentPayPeriod || verifyLoading) return;
    setVerifyLoading(true);
    setVerifyJob(null);
    verifyAbortRef.current?.abort();
    const controller = new AbortController();
    verifyAbortRef.current = controller;
    const fileName = `VistaPostVerification_Week${currentPayPeriod.WeekNumber}_${currentPayPeriod.AccountingYear}.xlsx`;
    try {
      await downloadVistaPostVerificationReport(currentPayPeriodID, fileName, setVerifyJob, controller.signal);
      showToast(`Vista Post Verification ready — Week ${week}.`);
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') return;
      const msg = error instanceof Error ? error.message : 'Could not generate the Vista Post Verification report.';
      showError('Vista post verification failed', msg);
    } finally {
      setVerifyLoading(false);
      setVerifyJob(null);
    }
  };

  // Not gated on sageItems.length: reconciling a period that has already been exported is the point.
  const handleSageReconcile = async () => {
    if (!currentPayPeriod || sageReconLoading) return;
    setSageReconLoading(true);
    setSageReconJob(null);
    sageReconAbortRef.current?.abort();
    const controller = new AbortController();
    sageReconAbortRef.current = controller;
    const fileName = `SageReconciliation-${currentPayPeriod.AccountingYear}-Week${currentPayPeriod.WeekNumber}.xlsx`;
    try {
      await downloadSageReconciliationReport(currentPayPeriodID, fileName, setSageReconJob, controller.signal);
      showToast(`Sage reconciliation ready — Week ${week}.`);
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') return;
      showSageBlocked(error, 'Could not reconcile with Sage. Please try again.');
    } finally {
      setSageReconLoading(false);
      setSageReconJob(null);
    }
  };

  useEffect(() => () => {
    verifyAbortRef.current?.abort();
    sageReconAbortRef.current?.abort();
  }, []);

  const handleSagePush = () => {
    if (!currentPayPeriod || sageExcelLoading || sagePushLoading) return;
    setSageModalMode('api');
  };

  // `force` arrives only from the modal's "Export again" confirm step.
  const handleSagePushConfirm = async (ids: number[], force?: boolean) => {
    if (sagePushLoading) return;
    setSagePushLoading(true);
    try {
      const result = await pushSageRows(currentPayPeriodID, ids, force === true);
      setSageModalMode(null);
      const empN = result.employeeCount ?? ids.length;
      setCMConfig({
        variant: 'success',
        icon: <CheckCircle size={22} />,
        title: 'Exported to Sage',
        subtitle: `${result.rowCount} row${result.rowCount === 1 ? '' : 's'} pushed to Sage for Week ${week} (${empN} employee${empN === 1 ? '' : 's'}).`,
        detail: (result.message || result.warning) ? (
          <>
            {result.message && <div>{result.message}</div>}
            {result.warning && (
              <div className="tr-sagewarn"><AlertTriangle size={15} /> {result.warning}</div>
            )}
          </>
        ) : undefined,
      });
    } catch (error) {
      setSageModalMode(null);
      showSageBlocked(error, 'Could not push timesheets to Sage. Please try again.');
    } finally {
      setSagePushLoading(false);
      // Success, 409, 502 (recorded unconfirmed) — every outcome may have moved the record.
      setSageStatusNonce((n) => n + 1);
    }
  };

  const handlePrintAll = async () => {
    if (!currentPayPeriod || printAllLoading) return;
    printAllAbortRef.current?.abort();
    const controller = new AbortController();
    printAllAbortRef.current = controller;

    // Honor the location filter, like the legacy Print All (filename uses the alt ID).
    let locationID: number | undefined;
    let locationLabel: string | undefined;
    if (locationFilter !== 'all') {
      const loc = locations.find((l) => l.locationID === locationFilter);
      if (loc) {
        locationID = loc.locationID;
        locationLabel = loc.altLocationID || loc.name;
      }
    }

    setPrintAllLoading(true);
    setPrintAllJob(null);
    try {
      const ok = await printTimesheetBatchAll(
        currentPayPeriodID,
        currentPayPeriod,
        controller.signal,
        setPrintAllJob,
        locationID,
        locationLabel
      );
      if (ok) showToast(`Combined timesheet PDF ready — Week ${week}${locationLabel ? ` · ${locationLabel}` : ''}.`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Could not generate the combined PDF. Please try again.';
      showError('Print all failed', msg);
    } finally {
      if (printAllAbortRef.current === controller) printAllAbortRef.current = null;
      setPrintAllLoading(false);
      setPrintAllJob(null);
    }
  };

  const handlePrintSupervisor = async (g: Supervisor) => {
    if (!currentPayPeriod || printingSupId != null) return;
    const n = g.employees?.length ?? g.count ?? 0;
    setPrintingSupId(g.employeeID);
    showToast(`Preparing ${n} timesheet${n === 1 ? '' : 's'} for ${g.name.last}, ${g.name.first} — Week ${week}…`);
    try {
      const ok = await printTimesheetBatch(g.employeeID, currentPayPeriodID, g.altEmployeeID ?? String(g.employeeID), currentPayPeriod);
      if (!ok) showError('Print failed', 'Could not generate the combined PDF. Please try again.');
    } catch {
      showError('Print failed', 'Could not generate the combined PDF. Please try again.');
    } finally {
      setPrintingSupId(null);
    }
  };

  // Wallcraft Spectrum export — the modal owns preview/xlsx/push; the page only raises the
  // success card, in the same shape handleSagePushConfirm uses for the other ERP push.
  const handleSpectrumPushed = (result: SpectrumPushResult) => {
    setSpectrumCrew(null);
    // A push moves the "as pushed" side — recheck what still differs.
    setDriftNonce((n) => n + 1);
    // The server's own message carries the PO phase counts, so it wins; the composed
    // line is only the fallback for an API build that doesn't send one.
    const summary = result.message?.trim()
      || `${result.rowCount} row${result.rowCount === 1 ? '' : 's'} for ${result.employeeCount} employee${result.employeeCount === 1 ? '' : 's'} sent as batch ${result.batchCode} — Week ${week}.`;
    const warnings = (result.warnings ?? []).filter((w) => !!w?.trim());
    setCMConfig({
      variant: 'success',
      icon: <CheckCircle size={22} />,
      title: 'Pushed to Spectrum',
      subtitle: warnings.length ? `${summary} ${warnings.join(' ')}` : summary,
    });
  };

  // ── WCDP Paycor file (TAS-682) ──────────────────────────────────────────────
  // Preview first (who is in the file, who is left out), confirm, then generate. The file is
  // recorded server-side when generated, so the status nonce is bumped after every attempt.
  const runWcdpPaycorFile = async () => {
    if (!currentPayPeriodID) return;
    setPaycorBusy('file');
    try {
      await downloadWcdpPaycorFile(currentPayPeriodID);
      showToast(`Paycor file ready — Week ${week}.`);
    } catch (error) {
      const msg = error instanceof Error && error.message ? error.message : 'Could not generate the Paycor file.';
      showError('Paycor file not generated', msg);
    } finally {
      setPaycorBusy(null);
      setWcdpStatusNonce((n) => n + 1);
    }
  };

  const handleWcdpPaycor = async () => {
    if (!currentPayPeriodID || paycorBusy !== null) return;
    setPaycorBusy('preview');
    let model;
    try {
      model = wcdpPaycorConfirmModel(await getWcdpPaycorPreview(currentPayPeriodID));
    } catch (error) {
      const msg = error instanceof Error && error.message ? error.message : 'Could not load the Paycor preview.';
      showError('Paycor file not generated', msg);
      return;
    } finally {
      setPaycorBusy(null);
    }
    if (!model.canGenerate) {
      showError(
        'Nothing to put in the Paycor file',
        `No Payroll Approved employee has been batch exported for Week ${week} yet.`
          + (model.approvedNotExportedCount ? ` ${plural(model.approvedNotExportedCount, 'approved employee')} not exported.` : ''),
      );
      return;
    }
    setCMConfig({
      variant: 'warning',
      icon: <AlertTriangle size={22} />,
      title: 'Generate Paycor File?',
      subtitle: `Week ${week} · ${plural(model.includedCount, 'batch-exported approved employee')}`,
      detail: (
        <div className="tr-bulkgroup">
          <ul className="tr-bulklist">
            <li><b>{plural(model.includedCount, 'employee')}</b> included (batch exported and Payroll Approved).</li>
            <li>
              <b>{plural(model.approvedNotExportedCount, 'approved employee')}</b> not yet batch exported
              {model.approvedNotExportedCount ? ' — left out of this file.' : '.'}
            </li>
          </ul>
          {model.exportedNotApproved.length > 0 && (
            <div className="tr-sagewarn">
              <AlertTriangle size={15} />
              <span>
                <b>
                  {plural(model.exportedNotApproved.length, 'employee')} batch exported but no longer Payroll
                  Approved — not in this file:
                </b>
                <ul className="wx-list-plain">
                  {model.exportedNotApproved.map((n, i) => <li key={`${n}-${i}`}>{n}</li>)}
                </ul>
              </span>
            </div>
          )}
        </div>
      ),
      confirmLabel: 'Generate file',
      onConfirm: () => { setCMConfig(null); void runWcdpPaycorFile(); },
    });
  };

  // ── Wallcraft Payroll Summary (as pushed to Spectrum, whole week) ───────────
  const crewLabelForDrift = (d: SpectrumDriftEmployee): string => {
    const sup = (allSupervisorData.payrollApproved ?? []).find((g) => g.employeeID === d.supervisorID);
    const supName = sup ? `${sup.name.last}, ${sup.name.first}` : '';
    return [d.crewCode, supName].filter(Boolean).join(' · ') || `Crew ${d.supervisorID}`;
  };

  const runWallcraftDownload = async (kind: 'summary' | 'detail') => {
    if (!currentPayPeriod) return;
    setWcDownloading(kind);
    const weekTag = `Week${currentPayPeriod.WeekNumber}_${currentPayPeriod.AccountingYear}`;
    const spec = kind === 'summary'
      ? {
          download: downloadSpectrumSummaryFile,
          fileName: `WallcraftPayrollSummary_${weekTag}.xlsx`,
          label: 'Weekly Summary',
        }
      : {
          download: downloadSpectrumDetailFile,
          fileName: `WallcraftPayrollDetail_${weekTag}.xlsx`,
          label: 'Payroll Detail',
        };
    try {
      await spec.download(currentPayPeriodID, spec.fileName);
      showToast(`${spec.label} ready — Week ${week}.`);
    } catch (error) {
      // A 404 carries the server's "No timesheets have been exported to Spectrum for Week N yet."
      const msg = error instanceof Error && error.message
        ? error.message
        : `Could not generate the ${spec.label}. Please try again.`;
      showError(`${spec.label} not generated`, msg);
    } finally {
      setWcDownloading(null);
    }
  };

  // Checks for timecards that changed after their Spectrum push first. The file itself is
  // always as-pushed; the prompt only makes sure payroll knows it no longer matches.
  const handleWallcraftDownload = async (kind: 'summary' | 'detail') => {
    if (!currentPayPeriod || wcDownloading !== null) return;
    setWcDownloading(kind);
    let changed: SpectrumDriftEmployee[] = [];
    try {
      changed = (await getSpectrumChanges(currentPayPeriodID)).employees;
      setSpectrumDrift(changed);
    } catch (err) {
      // Advisory only — a failed check must not block the (as-pushed) summary.
      console.warn('Spectrum change check failed; generating the summary without it.', err);
    }
    setWcDownloading(null);
    if (changed.length === 0) {
      void runWallcraftDownload(kind);
      return;
    }
    setCMConfig({
      variant: 'warning',
      icon: <AlertTriangle size={22} />,
      title: 'Timecards changed after the Spectrum push',
      subtitle: `${changed.length} employee${changed.length === 1 ? '' : 's'} · Week ${week}`,
      detail: (
        <div className="tr-bulkgroup">
          <div className="tr-bulkgrouphd">Pushed → current</div>
          <ul className="tr-bulklist tr-driftlist">
            {changed.map((d) => {
              const last = sortedDriftEdits(d)[0];
              return (
                <li key={d.employeeID}>
                  <b>{crewLabelForDrift(d)} · {d.name} #{d.altEmployeeID}</b>
                  {!d.stillApproved && <span className="ss-chip tr-drift">No longer Payroll Approved</span>}
                  <div>{formatDriftTotalsShift(d)}</div>
                  {last && <div className="tr-driftedit">Last edit: {formatDriftEdit(last)}</div>}
                </li>
              );
            })}
          </ul>
          <p className="tr-driftnote">The summary reflects what was pushed to Spectrum.</p>
        </div>
      ),
      confirmLabel: 'Generate anyway',
      onConfirm: () => { setCMConfig(null); void runWallcraftDownload(kind); },
    });
  };

  // Keep the fixed console action bar aligned to the .etrc container: it stays pinned
  // to the viewport bottom (original position) but its left/width exactly match the
  // console box (box-sizing:border-box so padding doesn't over-run). A rAF follow-loop
  // re-measures .etrc each frame while it's changing, so the bar tracks the content
  // smoothly during the sidebar collapse/expand transition (a plain ResizeObserver
  // update lags the CSS transition by a frame and the bar visibly trails).
  const trbRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const root = trbRef.current;
    if (!root) return;
    let lastL = NaN, lastW = NaN, stable = 0;
    const tick = () => {
      const etrc = root.querySelector('.etrc') as HTMLElement | null;
      if (!etrc) { rafRef.current = null; return; }
      const r = etrc.getBoundingClientRect();
      const l = Math.round(r.left), w = Math.round(r.width);
      if (l !== lastL || w !== lastW) {
        root.style.setProperty('--etrc-bar-left', `${l}px`);
        root.style.setProperty('--etrc-bar-width', `${w}px`);
        lastL = l; lastW = w; stable = 0;
      } else {
        stable += 1;
      }
      // Keep following for a few settled frames past the last change, then idle.
      rafRef.current = stable < 5 ? requestAnimationFrame(tick) : null;
    };
    const sync = () => {
      stable = 0;
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    const etrc = root.querySelector('.etrc');
    if (etrc) ro.observe(etrc);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [view, timesheetData]);

  // ── Location helpers ──────────────────────────────────────────────────────
  const locationName = useCallback((locationID?: number): string => {
    const loc = locations.find((l) => l.locationID === locationID);
    return loc ? loc.name : '—';
  }, [locations]);

  // ── Summary strip capability (TAS-263/264) ──────────────────────────────────
  // The strip has its OWN tenant gate: the design's capability matrix is normative and
  // most legacy switches don't map (showMealPenaltyWarning is false on 2020PH/WCDP while
  // the design puts meal chips on every tenant). Every key defaults to true when absent;
  // sickRate falls back to the one legacy flag that does map.
  //
  // TWO legacy flags are honoured rather than overridden. sickRate as above, and
  // avgRate: showAvgHourlyRate is the tenant's standing answer to "may this client see a
  // derived $/hour figure at all", and the piece rate on the money cell is exactly that
  // number. The strip originally showed it everywhere on the design's authority; a tenant
  // that has the rate switched off on every other screen should not meet it here.
  // Declared here (above the filter pipeline) because the flag facet gates on it.
  const cap = useMemo(() => {
    const s = config.summaryStrip ?? {};
    const on = (v?: boolean) => v !== false;
    return {
      enabled: on(s.enabled),
      holiday: on(s.holiday),
      trueup: on(s.trueup),
      supp: on(s.supp),
      pw: on(s.pw),
      sickRate: s.sickRate ?? !config.hideSickClaimHourlyRate,
      meal: on(s.meal),
      misc: on(s.misc),
      cs: on(s.cs),
      rollupClaimed: on(s.rollupClaimed),
      avgRate: config.showAvgHourlyRate === true,
      // TAS-679: NOT a summaryStrip key (those default ON) — the Modified Duty chip and
      // facet follow the page's own tenant gate, so only WCDP ever sees them.
      md: config.showModifiedDuty === true,
    };
  }, []);

  // Wallcraft production totals (B4, design copy rule): with the tenant flag on, the
  // board's claim surfaces read as production totals rather than piece claims. Copy
  // only — no restructuring, and off-tenant every string is exactly what it was.
  const productionPayOn = config.productionPay?.enabled === true;

  // Unsubmitted carries no snapshot by design, so the strip collapses to today's
  // identity row there (and wherever a tenant switches the strip off entirely). The
  // flag facet is skipped in exactly the same places — with no snapshot there is
  // nothing to facet on, and a bar of zero-count chips would only be noise.
  const stripOff = useCallback(
    (bucket: StatusKey): boolean => !cap.enabled || bucket === 'unsubmitted',
    [cap]
  );

  // Only the flags the tenant actually supports become facets.
  const facetDefs = useMemo(() => FLAG_DEFS.filter((f) => !f.cap || cap[f.cap]), [cap]);

  // The facet is live only where it can mean something (see stripOff).
  const flagsActive = flagFilter.size > 0 && !stripOff(selectedBucket);

  // Location + Supervisor filtered ("baseGroups") — drives the pipeline counts,
  // the supervisor <select> options, the list, and the review queue.
  const matchesBase = useCallback(
    (g: ApproverGroup) =>
      // An approval group is not a person and carries no location, so a location filter
      // narrows its MEMBERS' work, not the group — hiding the whole row would silently
      // drop timesheets that are genuinely in the chosen location.
      (locationFilter === 'all' || g.locationID === locationFilter || g.kind === 'approvalGroup') &&
      (supervisorFilter === 'all' || g.employeeID === supervisorFilter),
    [locationFilter, supervisorFilter]
  );

  const filteredGroups = useCallback(
    (bucket: StatusKey): ApproverGroup[] => (allSupervisorData[bucket] ?? []).filter(matchesBase),
    [allSupervisorData, matchesBase]
  );

  // + inline text filter ("textGroups") — narrows the visible list and the review
  // queue only; bucket counts ignore the text layer. This is also the set the facet
  // COUNTS are computed over, so the facet composes with the other filters (and its
  // own counts don't move as flags are toggled).
  const textGroups = useCallback((bucket: StatusKey): ApproverGroup[] => {
    const t = textFilter.trim().toLowerCase();
    const gs = filteredGroups(bucket);
    if (!t) return gs;
    return gs
      .map((g) => ({
        ...g,
        employees: (g.employees ?? []).filter((e) =>
          `${e.name.first} ${e.name.last}`.toLowerCase().includes(t)
          || `${e.name.last}, ${e.name.first}`.toLowerCase().includes(t)
          || (e.altEmployeeID ?? '').toLowerCase().includes(t)),
      }))
      .filter((g) => g.employees.length > 0);
  }, [filteredGroups, textFilter]);

  /** ANY-of over the selected flags. A row without a usable snapshot is held out
   *  whenever a flag is active — an unknown snapshot cannot answer a yes/no question,
   *  and silently letting it through would assert a `false` the data doesn't support. */
  const matchesFlags = useCallback((e: SupervisorsEmployee): boolean => {
    if (flagFilter.size === 0) return true;
    return facetDefs.some((f) => {
      if (!flagFilter.has(f.k)) return false;
      // A row-level facet reads live fields, so it can judge a row with no snapshot.
      if (f.rowTest) return f.rowTest(e);
      return isUsable(e) && f.test(e.summary as TimesheetSummary);
    });
  }, [flagFilter, facetDefs]);

  // + the flag facet ("fgroups") — the final displayed set. Everything downstream
  // (list, note count, "Review N one-by-one", per-crew queues, crew roll-ups) reads
  // this, so they all track the facet without further wiring.
  const fgroups = useCallback((bucket: StatusKey): ApproverGroup[] => {
    const gs = textGroups(bucket);
    if (flagFilter.size === 0 || stripOff(bucket)) return gs;
    return gs
      .map((g) => ({ ...g, employees: (g.employees ?? []).filter(matchesFlags) }))
      .filter((g) => g.employees.length > 0);
  }, [textGroups, flagFilter, stripOff, matchesFlags]);

  // Sage picker rows — the payroll-approved crews currently in view (honors the
  // location/supervisor/text filters), flattened with the parent group's supervisor name.
  const sageItems = useMemo<SageExportItem[]>(() =>
    fgroups('payrollApproved').flatMap((g) =>
      (g.employees ?? []).map((e) => ({
        employeeID: e.employeeID,
        altEmployeeID: e.altEmployeeID,
        name: e.name,
        supervisorName: `${g.name.last}, ${g.name.first}`,
      }))), [fgroups]);

  /** Distinct employees across a set of groups. Identity everywhere but the 1st
   *  Approval stage, where one timesheet is listed under every approver still owing a
   *  signature — summing the group lengths there counts signatures, not timesheets. */
  const distinctCount = useCallback(
    (gs: ApproverGroup[]): number =>
      new Set(gs.flatMap((g) => (g.employees ?? []).map((e) => e.employeeID))).size,
    []
  );

  /** How many employees are listed under MORE THAN ONE group — the gap between the
   *  rows on screen and the timesheets they represent. Always 0 outside the 1st
   *  Approval stage. */
  const multiGroupCount = useCallback((gs: ApproverGroup[]): number => {
    const times = new Map<number, number>();
    gs.forEach((g) => (g.employees ?? []).forEach((e) =>
      times.set(e.employeeID, (times.get(e.employeeID) ?? 0) + 1)));
    let n = 0;
    times.forEach((c) => { if (c > 1) n += 1; });
    return n;
  }, []);

  // The stage tile counts TIMESHEETS. The server's dedupedTimesheetCount is
  // pay-period-wide, so it stands in only while nothing narrows the base set;
  // otherwise (and on an API build that omits it) the board dedupes what it holds.
  const baseNarrowed = locationFilter !== 'all' || supervisorFilter !== 'all';
  const empCount = useCallback(
    (bucket: StatusKey): number => {
      const gs = filteredGroups(bucket);
      if (bucket === 'managerPending') {
        return !baseNarrowed && managerPendingDeduped != null ? managerPendingDeduped : distinctCount(gs);
      }
      // TAS-679: a WCDP Modified Duty timesheet is listed under its supervisor AND HR
      // until both sign, so Pending Approval dedupes too. Without repeats this is the sum.
      if (bucket === 'submitted' && gs.every((g) => Array.isArray(g.employees))) {
        return distinctCount(gs);
      }
      return gs.reduce((s, g) => s + (g.employees?.length ?? g.count ?? 0), 0);
    },
    [filteredGroups, baseNarrowed, managerPendingDeduped, distinctCount]
  );
  const supCount = useCallback(
    (bucket: StatusKey): number => filteredGroups(bucket).length,
    [filteredGroups]
  );
  // Timesheets in the visible list (post text/flag filters) — deduped, so it matches
  // the queue "Review N one-by-one" builds.
  const listCount = useCallback(
    (bucket: StatusKey): number => distinctCount(fgroups(bucket)),
    [fgroups, distinctCount]
  );

  const firstNonEmpty = useCallback(
    (): StatusKey | null => BUCKETS.find((b) => empCount(b.k) > 0)?.k ?? null,
    [empCount]
  );

  // Supervisors present this pay period for the current location — computed across
  // all buckets so the option list is stable when switching buckets. count is the
  // supervisor's employee count in the CURRENT bucket (0 when absent from it).
  const supervisorOptions = useMemo(() => {
    // The 1st Approval stage groups by APPROVER — a different domain from the
    // supervisor stages, so the two option lists never mix (an approver offered while
    // a supervisor stage is selected could only ever match zero rows).
    const sourceKeys: StatusKey[] = selectedBucket === 'managerPending'
      ? ['managerPending']
      : STATUS_KEYS.filter((k) => k !== 'managerPending');
    // An approval group is not a person, so it carries no employee number — it keeps its
    // group name and (when the EE# flag is on) sorts to the end with the unnumbered rows.
    const seen = new Map<number, { name: string; altEmployeeID?: string }>();
    sourceKeys.forEach((k) => (allSupervisorData[k] ?? [])
      .filter((g) => locationFilter === 'all' || g.locationID === locationFilter || g.kind === 'approvalGroup')
      .forEach((g) => seen.set(g.employeeID, {
        name: groupLabel(g),
        altEmployeeID: g.kind === 'approvalGroup' ? undefined : g.altEmployeeID,
      })));
    const bucketCounts = new Map<number, number>();
    (allSupervisorData[selectedBucket] ?? [])
      .filter((g) => locationFilter === 'all' || g.locationID === locationFilter || g.kind === 'approvalGroup')
      .forEach((g) => bucketCounts.set(g.employeeID, g.count ?? (g.employees?.length ?? 0)));
    return sortSupervisorOptions(
      [...seen.entries()].map(([id, s]) => ({ id, ...s, count: bucketCounts.get(id) ?? 0 })),
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [allSupervisorData, locationFilter, selectedBucket]);

  // Scope label used in the list note and the queue position bar.
  const describeScope = useCallback((): string => {
    const parts: string[] = [];
    // An approver's list is a queue, not a crew — the people in it are not theirs.
    const byApprover = BMAP[selectedBucket].groupWord === 'approver';
    if (supervisorFilter !== 'all') {
      const s = supervisorOptions.find((o) => o.id === supervisorFilter);
      // Plain name, never the numbered label — this reads as prose ("Smith, John’s crew").
      parts.push(`${s?.name ?? supervisorFilter}’s ${byApprover ? 'queue' : 'crew'}`);
    } else if (locationFilter !== 'all') {
      parts.push(locationName(locationFilter));
    } else {
      parts.push(byApprover ? 'all approvers' : 'all supervisors');
    }
    const t = textFilter.trim();
    if (t) parts.push(`“${t}”`);
    // Listed in facet order (not selection order) so the same set always reads the same.
    if (flagsActive) {
      parts.push(`flags: ${facetDefs.filter((f) => flagFilter.has(f.k)).map((f) => f.label.toLowerCase()).join(', ')}`);
    }
    return parts.join(' · ');
  }, [supervisorFilter, locationFilter, textFilter, supervisorOptions, locationName, flagsActive, facetDefs, flagFilter, selectedBucket]);

  // Drop the supervisor filter if it no longer belongs to the chosen location.
  const handleLocationChange = (v: number | 'all') => {
    setLocationFilter(v);
    if (supervisorFilter !== 'all' && v !== 'all') {
      const stillThere = STATUS_KEYS.some((k) => (allSupervisorData[k] ?? [])
        .some((g) => g.employeeID === supervisorFilter && (g.locationID === v || g.kind === 'approvalGroup')));
      if (!stillThere) setSupervisorFilter('all');
    }
  };

  const clearFilters = () => {
    setLocationFilter('all');
    setSupervisorFilter('all');
    setTextFilter('');
    setFlagFilter(new Set());
  };

  const clearFlags = () => setFlagFilter(new Set());

  const toggleFlag = (k: string) => setFlagFilter((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  // ── Load all bucket data ────────────────────────────────────────────────────
  const loadAllBuckets = useCallback(async () => {
    if (!currentPayPeriodID) return;
    setBucketsLoading(true);
    // Approval Access (WP8): the server scopes the supervisor lists from the MSAL session
    // (admin/ViewAllEmployees see everyone; supervisors see their scope). No scope param.
    try {
      const [unsubmitted, managerPending, submitted, supervisorApproved, payrollApproved] = await Promise.all([
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.Unsubmitted),
        // Grouped by approver, not supervisor — and skipped entirely for tenants
        // that do not run the stage, so no request is made on their behalf.
        managerApprovalOn
          ? getManagerPendingApprovers(currentPayPeriodID)
          : Promise.resolve({ groups: [] as ApproverGroup[], dedupedTimesheetCount: undefined }),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.Submitted),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.SuperApproved),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.PayApproved),
      ]);
      setAllSupervisorData({
        unsubmitted, managerPending: managerPending.groups, submitted, supervisorApproved, payrollApproved,
      });
      setManagerPendingDeduped(managerPending.dedupedTimesheetCount ?? null);
    } catch {
      setAllSupervisorData({
        unsubmitted: [], managerPending: [], submitted: [], supervisorApproved: [], payrollApproved: [],
      });
      setManagerPendingDeduped(null);
    } finally {
      setBucketsLoading(false);
      setBucketsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPayPeriodID]);

  useEffect(() => {
    loadAllBuckets();
  }, [loadAllBuckets]);

  // ── Default selection / fallback when a stage empties (week or filter change) ─
  // Skips while ANY reviewIntent is pending — that effect owns the selection. An
  // intent can now arrive at any time, not only on mount (the global header search
  // sets one while this page is already open), so this gates on the intent itself
  // rather than on a first-mount latch.
  //
  // reviewIntent is read but deliberately NOT a dependency: this effect answers
  // "the data or filters moved, is the selected stage still viable?", and clearing a
  // consumed intent is neither. Depending on it would re-run the moment the intent
  // nulls and bounce the user off a deliberately-selected empty stage — which is
  // exactly what a zero-count stat card on Home navigates to.
  useEffect(() => {
    if (reviewIntent) return;
    setSelectedBucket((prev) => (empCount(prev) > 0 ? prev : (firstNonEmpty() ?? prev)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSupervisorData, locationFilter, supervisorFilter]);

  // ── Consume reviewIntent whenever one arrives and buckets are loaded ─────────
  // Gate on bucketsLoaded (not bucketsLoading): the latter starts false, so the
  // effect would otherwise fire against empty data before the first fetch resolves.
  //
  // Deliberately NOT latched to first mount. The global header search sets an intent
  // while this page is already mounted, and App.tsx's `${currentPage}-${reloadNonce}`
  // key does not change on setPage-to-the-same-page — so a latch would swallow every
  // intent after the first, silently and without clearing it. Self-terminating anyway:
  // the effect nulls the intent it just consumed, so there is no loop.
  useEffect(() => {
    if (!bucketsLoaded || !reviewIntent) return;
    const bucket = reviewIntent.bucket ?? firstNonEmpty() ?? 'unsubmitted';
    setSelectedBucket(bucket);
    if (reviewIntent.employeeID) {
      openEmployeeIntent(bucket, reviewIntent);
    }
    setReviewIntent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketsLoaded, reviewIntent]);

  // ── Load review data for a queue entry ──────────────────────────────────────
  const lastLoadedEmployeeRef = useRef<number | null>(null);
  const loadReview = useCallback(async (entry: QueueEntry, ppID: number) => {
    // Clear the console when switching to a different employee (so Prev/Next never
    // flashes the previous person's header); keep it for same-employee week changes
    // so the console can show its subtle in-place week-loading overlay instead.
    if (lastLoadedEmployeeRef.current !== entry.employeeID) {
      setTimesheetData(null);
    }
    lastLoadedEmployeeRef.current = entry.employeeID;
    setReviewLoading(true);
    setReviewError(null);
    setNoTimesheetForWeek(false);
    setActualStatus(null);
    setDriveTimeEntries([]);
    setTimeOffRequests([]);
    setTimeOffDetailMap({});
    setHolidays([]);
    setPayrollState(null);
    setPayrollPreview(null);
    setPayrollPreviewLoading(false);
    setSubmitWizardOpen(false);
    try {
      const pp = payPeriods.find((p) => p.PayPeriodID === ppID);
      const startDate = pp ? isoDateOnly(new Date(pp.StartDate)) : '';
      const endDate = pp ? isoDateOnly(new Date(pp.EndDate)) : '';

      const timeOffPromise = pp
        ? getEmployeeTimeOffForWeek(entry.employeeID, startDate, endDate)
        : Promise.resolve<TimeOffRequestSummary[]>([]);
      const holidayPromise = pp
        ? getEmployeeHolidays(entry.employeeID, startDate, endDate)
        : Promise.resolve<HolidayCalendarEntry[]>([]);

      const [tsResult, drive, tor, hols] = await Promise.all([
        getTimesheetForAnyStatus(entry.employeeID, ppID),
        getDriveTimeEntries(entry.employeeID, ppID),
        timeOffPromise,
        holidayPromise,
      ]);

      if (tsResult) {
        setTimesheetData(tsResult.employee);
        setActualStatus(tsResult.statusKey);
        // M1-B3: unsubmitted weeks loaded through timesheetEdit/state carry the
        // raw editable state; kick off the preview (server OT numbers — closed
        // decision 1) when the caller may actually call it (either write
        // permission opens /preview; without one the console falls back to the
        // legacy client display instead of dashing forever).
        const pState = tsResult.payrollState ?? null;
        setPayrollState(pState);
        if (pState && (canSubmitForEmployee(userRole) || canEditEntries(userRole))) {
          setPayrollPreviewLoading(true);
          TimesheetEditManager.loadPreview(entry.employeeID, ppID)
            .then((p) => {
              if (lastLoadedEmployeeRef.current !== entry.employeeID) return;
              if (!p.exists) setPayrollPreview(p);
            })
            .catch(() => { /* console falls back to the legacy client display */ })
            .finally(() => {
              if (lastLoadedEmployeeRef.current === entry.employeeID) setPayrollPreviewLoading(false);
            });
        }
      }
      setNoTimesheetForWeek(!tsResult);
      setDriveTimeEntries(drive);
      setTimeOffRequests(tor);
      setHolidays(hols);

      if (tor.length > 0) {
        const details = await Promise.all(
          tor.map((r) => getTimeOffRequestById(r.timeOffRequestID).catch(() => null))
        );
        setTimeOffDetailMap(buildTimeOffDetailMap(details.filter(Boolean) as TimeOffRequestDetail[]));
      }
    } catch {
      setReviewError('Failed to load timesheet data.');
    } finally {
      setReviewLoading(false);
    }
  }, [payPeriods, userRole]);

  // Load review for the current queue entry — covers entry, Prev/Next, and week change.
  useEffect(() => {
    if (view !== 'queue') return;
    const entry = queue[qi];
    if (!entry) return;
    loadReview(entry, payPeriodID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, qi, queue, payPeriodID]);

  // ── Queue building / opening ────────────────────────────────────────────────
  // Deduped by employee: in the 1st Approval stage one timesheet is listed under every
  // approver still owing a signature, and reviewing it three times is not three reviews.
  // A no-op in every other stage, where an employee appears in exactly one group.
  const buildQueueFromGroups = useCallback((gs: ApproverGroup[]): QueueEntry[] => {
    const q: QueueEntry[] = [];
    const seen = new Set<number>();
    gs.forEach((g) => {
      const supName = g.kind === 'approvalGroup' ? g.name.last : displayName(g.name);
      (g.employees ?? []).forEach((e: SupervisorsEmployee) => {
        if (seen.has(e.employeeID)) return;
        seen.add(e.employeeID);
        q.push({
          employeeID: e.employeeID,
          altEmployeeID: e.altEmployeeID,
          name: e.name,
          supervisorName: supName,
          // An approval group is not a supervisor — its negated group ID must never
          // reach Change Supervisor, so the entry carries no supervisorID.
          supervisorID: g.kind === 'approvalGroup' ? 0 : g.employeeID,
        });
      });
    });
    return q;
  }, []);

  const startQueue = useCallback((bucket: StatusKey, q: QueueEntry[], startIdx: number, scope: string) => {
    setQueue(q);
    setQi(Math.max(0, startIdx));
    setOrigin({ payPeriodID: currentPayPeriodID, bucket, scope });
    setApprovedIDs(new Set());
    setView('queue');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPayPeriodID]);

  const openQueue = useCallback((bucket: StatusKey, employeeID: number, gs: ApproverGroup[], scope: string): boolean => {
    const q = buildQueueFromGroups(gs);
    const idx = q.findIndex((x) => x.employeeID === employeeID);
    if (idx < 0) return false;
    startQueue(bucket, q, idx, scope);
    return true;
  }, [buildQueueFromGroups, startQueue]);

  // Row click — queue is the CURRENT filtered set, starting at that employee.
  const handleRowClick = (emp: SupervisorsEmployee) => {
    openQueue(selectedBucket, emp.employeeID, fgroups(selectedBucket), describeScope());
  };

  // "Review N one-by-one" (filter bar) — the whole filtered list, spanning supervisors.
  const reviewFiltered = () => {
    const q = buildQueueFromGroups(fgroups(selectedBucket));
    if (!q.length) return;
    startQueue(selectedBucket, q, 0, describeScope());
  };

  // "Review crew" / "Review queue" (group header) — one group, respecting the text filter.
  const reviewCrew = (g: ApproverGroup) => {
    const q = buildQueueFromGroups(fgroups(selectedBucket).filter((x) => x.employeeID === g.employeeID));
    if (!q.length) return;
    const kind = BMAP[selectedBucket].groupWord === 'approver' ? 'queue' : 'crew';
    startQueue(selectedBucket, q, 0, `${groupLabel(g)}’s ${kind}`);
  };

  /** Open an employee's review queue from a Home search intent. Tries the mapped
   *  bucket first, then any other bucket they appear in, and finally falls back to
   *  a single-person queue built from the carried search info (covers employees
   *  with no supervisor group, so navigation always lands on their timesheet). */
  const openEmployeeIntent = useCallback((preferredBucket: StatusKey, intent: ReviewIntent): void => {
    const employeeID = intent.employeeID;
    if (!employeeID) return;
    if (openQueue(preferredBucket, employeeID, allSupervisorData[preferredBucket] ?? [], 'all supervisors')) return;
    for (const b of STATUS_KEYS) {
      if (b !== preferredBucket && openQueue(b, employeeID, allSupervisorData[b] ?? [], 'all supervisors')) return;
    }
    // Fallback: single-entry queue. loadReview fetches the timesheet by
    // employeeID + payPeriod, so the console renders regardless of bucket membership.
    if (intent.name) {
      // Built outside any crew group, so there is no supervisorID — Change Supervisor
      // stays hidden on this entry rather than guessing one.
      startQueue(preferredBucket, [{
        employeeID,
        altEmployeeID: intent.altEmployeeID ?? '',
        name: intent.name,
        supervisorName: '',
        supervisorID: 0,
      }], 0, 'all supervisors');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQueue, startQueue, allSupervisorData]);

  const backToBoard = () => {
    if (origin) setSelectedBucket(origin.bucket);
    setView('board');
    setTimesheetData(null);
    setActualStatus(null);
    lastLoadedEmployeeRef.current = null;
  };

  // ── Queue navigation (post-approve auto-advance only — the manual Prev/Next/
  //    dots/Skip controls were removed; the console owns all actions) ──────────
  const advanceToNextUnapproved = (approved: Set<number>) => {
    let ni = -1;
    for (let i = qi + 1; i < queue.length; i++) if (!approved.has(queue[i].employeeID)) { ni = i; break; }
    if (ni < 0) for (let i = 0; i < queue.length; i++) if (!approved.has(queue[i].employeeID)) { ni = i; break; }
    if (ni >= 0 && ni !== qi) setQi(ni);
  };

  // ── Optimistic bucket move ──────────────────────────────────────────────────
  const optimisticMove = (from: StatusKey, to: StatusKey, employeeID: number) => {
    setAllSupervisorData((prev) => {
      const src = (prev[from] ?? []).map((g) => ({ ...g, employees: [...(g.employees ?? [])] }));
      let moved: SupervisorsEmployee | null = null;
      let supMeta: Supervisor | null = null;
      for (const g of src) {
        const i = g.employees.findIndex((e) => e.employeeID === employeeID);
        if (i >= 0) { moved = g.employees.splice(i, 1)[0]; supMeta = g; break; }
      }
      if (!moved || !supMeta) return prev;
      const newSrc = src
        .filter((g) => (g.employees?.length ?? 0) > 0)
        .map((g) => ({ ...g, count: g.employees.length }));
      const dst = (prev[to] ?? []).map((g) => ({ ...g, employees: [...(g.employees ?? [])] }));
      let tg = dst.find((g) => g.employeeID === supMeta!.employeeID);
      if (!tg) { tg = { ...supMeta, employees: [] }; dst.push(tg); }
      tg.employees.push(moved);
      const newDst = dst.map((g) => ({ ...g, count: g.employees.length }));
      return { ...prev, [from]: newSrc, [to]: newDst };
    });
  };

  // ── Time-off approve / deny ─────────────────────────────────────────────────
  const updateDetailMapStatus = (id: number, status: TimeOffRequestStatus) => {
    setTimeOffDetailMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((date) => {
        next[date] = next[date].map((e) => (e.requestID === id ? { ...e, status } : e));
      });
      return next;
    });
  };
  // Unpaid Time (TAS-586): a 422 PAID_NOT_EXHAUSTED offers "Approve anyway" for this one request,
  // re-sent with the override; any other refusal shows the API's reason when it gave one.
  const handleApproveTimeOff = async (id: number, overrideExhausted = false) => {
    try {
      await approveTimeOffRequest(id, undefined, overrideExhausted ? { overrideExhausted: true } : undefined);
      setTimeOffRequests((prev) => prev.map((r) => (r.timeOffRequestID === id ? { ...r, status: 'APPROVED' } : r)));
      updateDetailMapStatus(id, 'APPROVED');
    } catch (err) {
      if (!overrideExhausted && isPaidNotExhaustedError(err)) {
        setCMConfig({
          variant: 'warning',
          icon: <AlertTriangle size={22} />,
          title: 'Paid time isn’t used up',
          subtitle: 'This policy asks for paid time off to be used before unpaid time.',
          detail: <PaidOverrideDetail error={err} />,
          confirmLabel: 'Approve anyway',
          onConfirm: async () => {
            setCMConfig(null);
            await handleApproveTimeOff(id, true);
          },
        });
        return;
      }
      showError('Failed to approve time-off request.', refusalMessageOf(err, 'Please try again or contact support.'));
    }
  };
  const handleDenyTimeOff = async (id: number) => {
    try {
      await denyTimeOffRequest(id);
      setTimeOffRequests((prev) => prev.map((r) => (r.timeOffRequestID === id ? { ...r, status: 'DENIED' } : r)));
      updateDetailMapStatus(id, 'DENIED');
    } catch { showError('Failed to deny time-off request.', 'Please try again or contact support.'); }
  };

  // ── Approve to payroll (queue behavior: no navigation, auto-advance) ─────────
  // `fromStatus` is the timesheet's real status at approval time, which drives the destination;
  // the card itself always lives in origin.bucket, so that stays the `from` for the move.
  // `toOverride` lets a caller name the destination bucket the server actually landed on, for the
  // cases where approval deliberately stops short of the usual next bucket (single-hop
  // /approveToPayroll answering with finalStatus 'SupervisorApproved' on a sick week).
  const onApproveSuccess = (fromStatus?: StatusKey | null, toOverride?: StatusKey) => {
    const emp = queue[qi];
    if (!emp || !origin) return;
    const newApproved = new Set(approvedIDs);
    newApproved.add(emp.employeeID);
    setApprovedIDs(newApproved);
    const to = toOverride ?? NEXT_BUCKET[fromStatus ?? origin.bucket] ?? 'payrollApproved';
    optimisticMove(origin.bucket, to, emp.employeeID);
    loadAllBuckets(); // background reconcile
    advanceToNextUnapproved(newApproved);
  };

  // ── Sick-claim derivations (rate panel + approve gating) ────────────────────
  const sickClaims: EmployeeSickClaim[] = (timesheetData?.days ?? []).flatMap((d) => d.employeeSickClaims ?? []);
  const sickClaimsWithMinutes = sickClaims.filter((sc) => sc.minutesClaimed > 0);
  const totalSickHours = sickClaimsWithMinutes.reduce((s, sc) => s + sc.minutesClaimed, 0) / 60;
  // A rate counts as "saved" only when every minute-bearing claim carries the same positive rate.
  const savedRateSet = new Set(sickClaimsWithMinutes.map((sc) => sc.hourlyAmount ?? 0));
  const savedSickRate = sickClaimsWithMinutes.length > 0 && savedRateSet.size === 1 && [...savedRateSet][0] > 0
    ? [...savedRateSet][0]
    : null;
  const hasUnratedSick = sickClaimsWithMinutes.some((sc) => (sc.hourlyAmount ?? 0) <= 0);
  // Panel visibility: payroll-pending timesheet, sick-rate UI enabled, sick claims present,
  // and the caller may approve in the Payroll Pending bucket (PayrollApproveTimesheets, or
  // admin). The rate is a payroll input that gates payroll approval — a supervisor who can
  // only approve the Submitted bucket has no business entering it, so the bar is hidden
  // rather than shown disabled.
  const canEnterSickRate = canApproveStage(userRole, 'supervisorApproved');
  const showSickRatePanel = !config.hideSickClaimHourlyRate
    && (actualStatus ?? origin?.bucket) === 'supervisorApproved'
    && sickClaims.length > 0
    && canEnterSickRate;

  // Mirror an already-saved rate into the panel input when a timesheet loads; blank otherwise
  // (no base-rate prefill — D8).
  useEffect(() => {
    setSickPanelRate(savedSickRate != null ? String(savedSickRate) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetData]);

  // Pull a server-provided message out of an axios error. The approve endpoints are
  // inconsistent: /approveToPayroll 400s with { message }, while other handlers use
  // { errorMessage }. Check both so the real reason (e.g. the sick-rate block) reaches the user.
  const apiErrorMessage = (err: any): string | undefined =>
    err?.response?.data?.errorMessage || err?.response?.data?.message;

  const doApproveToPayroll = async (timesheetID: number, hourlyRate?: number) => {
    setApproveLoading(true);
    try {
      if (hourlyRate !== undefined && timesheetData) {
        const allIDs = timesheetData.days.flatMap((d) =>
          (d.employeeSickClaims ?? []).map((sc: EmployeeSickClaim) => sc.employeeSickClaimID)
        );
        if (allIDs.length > 0) {
          // Rate validation (WP7) fails as a HTTP-200 { success:false, errorMessage } — surface
          // it and abort rather than proceeding to approve with an unsaved rate.
          const rateResult = await updateSickClaimAmount(allIDs, hourlyRate);
          if (rateResult && rateResult.success === false) {
            showError('Sick hourly rate not saved', rateResult.errorMessage || 'Could not save the sick hourly rate. Please check the amount and try again.');
            return;
          }
        }
      }
      // Route by the timesheet's current status: a payroll-pending (SupervisorApproved) timesheet
      // moves to PayrollApproved via /payroll/approve, which enforces the WP6 sick-rate block; the
      // single-hop approveToPayroll only accepts Submitted timesheets. Tenants without
      // useSingleHopPayrollApproval supervisor-approve instead and stop at Supervisor Approved.
      const currentStatus = actualStatus ?? origin?.bucket;
      // Where the card actually landed, when the server declines to take it all the way.
      let landedBucket: StatusKey | undefined;
      let stoppedForSickRate = false;
      if (currentStatus === 'supervisorApproved') {
        const result = await payrollApproveTimesheet(timesheetID, userID);
        if (result && result.success === false) {
          showError('Payroll approval blocked', result.errorMessage || 'Could not approve the timesheet. Please try again.');
          return;
        }
      } else if (singleHop) {
        // The endpoint reports where it stopped. A week with an approved sick time-off request on
        // a tenant that always requires a manually entered sick rate (Wallcraft) halts in Payroll
        // Pending, so move the card there instead of optimistically to Payroll Approved.
        const result = await approveToPayroll(timesheetID, userID);
        if (result?.finalStatus === 'SupervisorApproved') {
          landedBucket = 'supervisorApproved';
          stoppedForSickRate = true;
        }
      } else {
        const result = await approveSupervisorOnly(timesheetID, userID);
        if (result && result.success === false) {
          showError('Approval blocked', result.errorMessage || 'Could not approve the timesheet. Please try again.');
          return;
        }
        // TAS-679 (WCDP Modified Duty): this approval filled the caller's slot, but another
        // required supervisor (HR) has not approved yet — the timesheet is still Submitted.
        // Not a move: say so, refresh the open timesheet's slots and reconcile the board.
        // `waiting` is only ever sent on a 2+ slot timesheet, so no other tenant lands here.
        if (isWaitingOnAnotherApprover(result)) {
          showToast(waitingApprovalText(result));
          const entry = queue[qi];
          if (entry) loadReview(entry, payPeriodID);
          loadAllBuckets();
          return;
        }
      }
      setSickRateModalOpen(false);
      if (stoppedForSickRate) showToast('Moved to Payroll Pending — sick hourly rate needed');
      onApproveSuccess(currentStatus, landedBucket);
    } catch (err) {
      showError('Approval failed', apiErrorMessage(err) || 'Could not approve the timesheet. Please try again.');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleApproveToPayroll = async () => {
    if (!timesheetData) return;
    // Prompt for the rate only while a minute-bearing sick claim still lacks a positive rate,
    // and only where the rate panel itself would show — i.e. the Payroll Pending
    // (supervisorApproved) bucket, with the permission and the tenant's rate UI enabled. A
    // Submitted-bucket approval never asks, including on a single-hop tenant where that
    // approval lands the timesheet straight in PayrollApproved; if the API's sick-rate block
    // rejects it, the error surfaces and payroll enters the rate from the Payroll Pending
    // bucket. Once a rate is saved (via the panel or a prior prompt), approve directly.
    if (hasUnratedSick && showSickRatePanel) {
      setSickRateModalOpen(true);
    } else {
      await doApproveToPayroll(timesheetData.timesheetID);
    }
  };

  const handleSickRateConfirm = async () => {
    const rate = parseFloat(sickRateInput);
    if (isNaN(rate) || rate <= 0) { showError('Invalid hourly rate', 'Please enter a valid rate greater than zero.'); return; }
    if (!timesheetData) return;
    await doApproveToPayroll(timesheetData.timesheetID, rate);
  };

  // ── Bulk payroll approve ("Approve All", ported from v1) ────────────────────
  // v1 gated this on the coarse `userRole.payroll || userRole.admin` booleans, which both
  // hid the button from roles the API would have let approve and showed it to roles that
  // 403 on every call. The gate here is the one the API actually enforces
  // (PayrollApproveTimesheets, admin implies it) — the same gate the rest of this page uses.
  const canBulkApprove = selectedBucket === 'supervisorApproved'
    && canApproveStage(userRole, 'supervisorApproved');

  type BulkPartition = {
    ready: SupervisorsEmployee[];
    blocked: SupervisorsEmployee[];  // pre-skipped: the snapshot says the sick rate is missing
    noID: SupervisorsEmployee[];     // no timesheetID projected — nothing to act on
  };

  // v1 pre-skipped on `amountClaimed === 0`, which also matches a claim of ZERO minutes and
  // so falsely skipped timesheets the server would have approved. `needsSickHourlyRate` is
  // the snapshot mirror of the server's actual block (minutes > 0 AND no positive rate).
  // A tenant that hides the sick rate entirely (NPM) never pre-skips — matching v1, and
  // matching the server, which drops its own block on the same tenants.
  //
  // Rows with NO snapshot (or a stale one) fall through to `ready` on purpose: supervisor
  // approval does not stamp a snapshot, so "no summary" is a normal state here and means
  // "unknown", not "fine". Those go to the server, which re-checks against live data; a
  // rejection comes back as a skip below. That is what makes the pre-screen safe.
  const partitionForBulk = useCallback((rows: SupervisorsEmployee[]): BulkPartition => {
    const ready: SupervisorsEmployee[] = [];
    const blocked: SupervisorsEmployee[] = [];
    const noID: SupervisorsEmployee[] = [];
    rows.forEach((e) => {
      if (!config.hideSickClaimHourlyRate && needsSickRate(e)) { blocked.push(e); return; }
      if (e.timesheetID == null) { noID.push(e); return; }
      ready.push(e);
    });
    return { ready, blocked, noID };
  }, []);

  const empLabel = (e: SupervisorsEmployee) => `${e.name.last}, ${e.name.first}`;

  const cancelBulkApprove = () => bulkAbortRef.current?.abort();

  const executeBulkApprove = async (part: BulkPartition, crewId: number | null) => {
    const { ready, blocked, noID } = part;
    const controller = new AbortController();
    bulkAbortRef.current = controller;
    setBulkRunning(true);
    setBulkCrewId(crewId);
    setBulkProgress({ done: 0, total: ready.length });

    const approved: SupervisorsEmployee[] = [];
    const skipped: { e: SupervisorsEmployee; reason: string }[] =
      blocked.map((e) => ({ e, reason: 'sick hourly rate not entered' }));
    const failed: { e: SupervisorsEmployee; reason: string }[] =
      noID.map((e) => ({ e, reason: 'no timesheet found for this week' }));
    let done = 0;
    let cancelled = false;

    // Shared work queue — BULK_APPROVE_CONCURRENCY workers draining one list, so a slow
    // timesheet never stalls the others behind a fixed batch boundary.
    const pending = [...ready];
    const worker = async () => {
      for (;;) {
        if (controller.signal.aborted) { cancelled = true; return; }
        const e = pending.shift();
        if (!e) return;
        try {
          const result = await payrollApproveTimesheet(e.timesheetID!, userID);
          // A business-rule rejection comes back as HTTP 200 { success:false }. v1 treated
          // every 2xx as a success and told payroll it had approved timesheets it had not.
          if (result && result.success === false) {
            const msg = result.errorMessage || 'Approval was rejected.';
            if (SICK_RATE_BLOCK_RE.test(msg)) skipped.push({ e, reason: 'sick hourly rate not entered' });
            else failed.push({ e, reason: msg });
          } else {
            approved.push(e);
          }
        } catch (err) {
          failed.push({ e, reason: apiErrorMessage(err) || 'Approval failed.' });
        } finally {
          done += 1;
          setBulkProgress({ done, total: ready.length });
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(BULK_APPROVE_CONCURRENCY, pending.length) }, () => worker())
      );
    } finally {
      if (bulkAbortRef.current === controller) bulkAbortRef.current = null;
      setBulkRunning(false);
      setBulkCrewId(null);
      setBulkProgress(null);
    }

    // Move what actually approved, then reconcile from the server.
    approved.forEach((e) => optimisticMove('supervisorApproved', 'payrollApproved', e.employeeID));
    if (approved.length > 0) loadAllBuckets();

    const nRemaining = ready.length - done;
    const line = (label: string, rows: { e: SupervisorsEmployee; reason: string }[]) => (
      <div className="tr-bulkgroup">
        <div className="tr-bulkgrouphd">{label}</div>
        <ul className="tr-bulklist">
          {rows.map(({ e, reason }) => (
            <li key={e.employeeID}><b>{empLabel(e)}</b> — {reason}</li>
          ))}
        </ul>
      </div>
    );

    // v1 wrote per-employee failures to the console only, so payroll never learned WHICH
    // employees had failed or why. Name them.
    setCMConfig({
      variant: failed.length > 0 ? 'error' : 'success',
      icon: failed.length > 0 ? <AlertCircle size={22} /> : <CheckCircle size={22} />,
      title: cancelled
        ? `Stopped — ${approved.length} approved`
        : `${approved.length} timesheet${approved.length === 1 ? '' : 's'} payroll approved`,
      subtitle: [
        skipped.length > 0 ? `${skipped.length} skipped` : '',
        failed.length > 0 ? `${failed.length} failed` : '',
        cancelled && nRemaining > 0 ? `${nRemaining} not attempted` : '',
      ].filter(Boolean).join(' · ') || undefined,
      detail: (skipped.length > 0 || failed.length > 0) ? (
        <>
          {skipped.length > 0 && line('Skipped', skipped)}
          {failed.length > 0 && line('Failed', failed)}
        </>
      ) : undefined,
    });
  };

  // Both entry points (the bucket bar and a crew header) land here. `rows` is already the
  // scope being acted on — v1 silently approved whatever its search box had narrowed the
  // list to; here the scope is named in the confirm.
  const startBulkApprove = (rows: SupervisorsEmployee[], scopeLabel: string, crewId: number | null) => {
    if (bulkRunning) return;
    const part = partitionForBulk(rows);
    if (part.ready.length === 0) {
      showError(
        'Nothing to approve',
        part.blocked.length > 0
          ? `All ${part.blocked.length} timesheet${part.blocked.length === 1 ? '' : 's'} here need a sick hourly rate first.`
          : 'No timesheets in this view are ready for payroll approval.'
      );
      return;
    }
    const n = part.ready.length;
    setCMConfig({
      variant: 'warning',
      icon: <AlertCircle size={22} />,
      title: `Payroll approve ${n} timesheet${n === 1 ? '' : 's'}?`,
      subtitle: `${scopeLabel} · Week ${week}`,
      detail: (
        <>
          <div>This approves {n} timesheet{n === 1 ? '' : 's'} to payroll. It cannot be undone from here — an approved timesheet has to be unlocked individually.</div>
          {part.blocked.length > 0 && (
            <div className="tr-bulkskip">
              {part.blocked.length} timesheet{part.blocked.length === 1 ? '' : 's'} will be skipped — sick hourly rate not entered.
            </div>
          )}
          {part.noID.length > 0 && (
            <div className="tr-bulkskip">
              {part.noID.length} row{part.noID.length === 1 ? '' : 's'} could not be matched to a timesheet and will be reported as failed.
            </div>
          )}
        </>
      ),
      confirmLabel: `Approve ${n}`,
      // Close first and run detached: the action bar owns progress and cancellation from
      // here, so the modal must not sit on "Processing…" for the length of the run.
      onConfirm: () => { setCMConfig(null); void executeBulkApprove(part, crewId); },
    });
  };

  // ── Sick-rate panel actions ─────────────────────────────────────────────────
  // Save Rate: persist the rate only (no approval), surface WP7 validation failures, then
  // refresh the console and clear this employee's board badge.
  // Save & Approve: same save (doApproveToPayroll saves first and aborts if the save fails),
  // then the payroll-approve routing.
  const handleSaveSickRate = async (approveAfter: boolean) => {
    if (!timesheetData || !canEnterSickRate) return;
    const rate = parseFloat(sickPanelRate);
    if (isNaN(rate) || rate <= 0) { showError('Invalid hourly rate', 'Please enter a valid rate greater than zero.'); return; }
    if (approveAfter) {
      await doApproveToPayroll(timesheetData.timesheetID, rate);
      return;
    }
    const allIDs = sickClaims.map((sc) => sc.employeeSickClaimID);
    if (allIDs.length === 0) return;
    setSickRateSaving(true);
    try {
      const result = await updateSickClaimAmount(allIDs, rate);
      if (result && result.success === false) {
        showError('Sick hourly rate not saved', result.errorMessage || 'Could not save the sick hourly rate. Please check the amount and try again.');
        return;
      }
      // Saved: reload the console so the hours × rate amounts reflect the update. The
      // board's blocking pill clears on the next bucket reconcile — it reads the
      // snapshot's needsSickHourlyRate, which the API re-stamps on save.
      const emp = queue[qi];
      if (emp) loadReview(emp, payPeriodID);
      showToast(`Sick hourly rate $${rate.toFixed(2)} saved.`);
    } catch (err) {
      showError('Sick hourly rate not saved', apiErrorMessage(err) || 'Could not save the sick hourly rate. Please try again.');
    } finally {
      setSickRateSaving(false);
    }
  };

  // ── Change Supervisor ───────────────────────────────────────────────────────
  // v1 showed this on Submitted + Supervisor Approved only (TimesheetPage), gated on the
  // coarse payroll/admin booleans. The status pair carries over; the gate is now the
  // dedicated ReassignTimesheetSupervisor permission the API enforces. The API also
  // requires the employee to be in the caller's scope, which the client cannot know —
  // that arm surfaces as a 403 with its reason.
  //
  // (The visibility gate itself lives with the other queue-derived values below, since
  // it reads `currentEntry`.)
  const openSupervisorModal = async () => {
    setSupPick(null);
    setSupModalOpen(true);
    if (supOptions.length > 0) return; // cached for the session
    setSupOptionsLoading(true);
    try {
      const list = await getSupervisorList();
      // getSupervisorList swallows errors and returns [] — the empty state below says so.
      setSupOptions(
        [...list].sort((a, b) =>
          `${a.name.last} ${a.name.first}`.localeCompare(`${b.name.last} ${b.name.first}`)
        )
      );
    } finally {
      setSupOptionsLoading(false);
    }
  };

  const doReassignSupervisor = async (
    target: { employeeID: number; name: { first: string; middle: string; last: string } }
  ) => {
    if (!timesheetData || !currentEntry) return;
    setSupSaving(true);
    try {
      const result = await reassignTimesheetSupervisor(timesheetData.timesheetID, target.employeeID, userID);
      if (result && result.success === false) {
        showError('Supervisor not changed', result.errorMessage || 'Could not change the supervisor. Please try again.');
        return;
      }
      setSupModalOpen(false);
      // The timesheet leaves one crew group and joins another, so the board must
      // reconcile — an optimistic move can't express a same-bucket regroup.
      const emp = queue[qi];
      if (emp) loadReview(emp, payPeriodID);
      loadAllBuckets();
      showToast(`Supervisor changed to ${displayName(target.name)} for Week ${week}.`);
    } catch (err) {
      showError('Supervisor not changed', apiErrorMessage(err) || 'Could not change the supervisor. Please try again.');
    } finally {
      setSupSaving(false);
    }
  };

  const confirmReassignSupervisor = (
    target: { employeeID: number; name: { first: string; middle: string; last: string } }
  ) => {
    if (!currentEntry) return;
    setCMConfig({
      variant: 'warning',
      icon: <AlertCircle size={22} />,
      title: 'Change this timesheet’s supervisor?',
      subtitle: `${displayName(currentEntry.name)} · Week ${week}`,
      detail: (
        <>
          <div>
            <b>{currentEntry.supervisorName || '—'}</b> → <b>{displayName(target.name)}</b>
          </div>
          <div className="tr-supnote">
            This moves the timesheet into {displayName(target.name)}’s queue for this pay period
            only. The employee’s regular supervisor does not change.
          </div>
        </>
      ),
      confirmLabel: 'Change supervisor',
      onConfirm: () => { setCMConfig(null); void doReassignSupervisor(target); },
    });
  };

  // ── Unlock / send back (payrollApproved queue + console Unlock button) ───────
  const handleSendBack = (timesheetID: number) => {
    const emp = queue[qi];
    const empName = timesheetData
      ? `${timesheetData.name.first} ${timesheetData.name.last}${timesheetData.altEmployeeID ? ` · #${timesheetData.altEmployeeID}` : ''}`
      : undefined;
    // Payroll Timesheet Modifications (M2-B): unlock zeroes HourlyAmount /
    // AmountClaimed for the whole week (resetEmployeeSickClaimAmounts), so a
    // just-entered sick rate is lost — warn when any claim carries an amount.
    const sickAmountsAtRisk = payrollEditingOn && (timesheetData?.days ?? []).some((d) =>
      (d.employeeSickClaims ?? []).some(
        (sc: EmployeeSickClaim) => (sc.amountClaimed ?? 0) > 0 || (sc.hourlyAmount ?? 0) > 0
      )
    );
    setCMConfig({
      variant: 'warning',
      icon: <Unlock size={22} />,
      title: 'Send timesheet back?',
      subtitle: sickAmountsAtRisk
        ? 'The timesheet will be unlocked and returned to the employee for corrections. Unlocking resets sick-claim rates/amounts for this week — they will need re-entry.'
        : 'The timesheet will be unlocked and returned to the employee for corrections.',
      detail: empName ? <div><strong>{empName}</strong></div> : undefined,
      confirmLabel: 'Send Back',
      onConfirm: async () => {
        await unlockTimesheet(timesheetID, userID);
        if (emp) {
          optimisticMove(actualStatus ?? origin?.bucket ?? 'payrollApproved', 'submitted', emp.employeeID);
          loadAllBuckets();
        }
        setCMConfig({
          variant: 'success',
          icon: <CheckCircle size={22} />,
          title: 'Timesheet sent back',
          subtitle: 'The employee can now make corrections and resubmit.',
        });
        if (emp) loadReview(emp, payPeriodID);
      },
    });
  };

  // ── Payroll Timesheet Modifications (M1-B3) — submit wizard + editor route ──
  // Success: popup + back to the board with the row optimistically moved out of
  // Unsubmitted (the submit stamps the summary snapshot server-side, so the
  // reconciling loadAllBuckets shows real numbers immediately).
  const handlePayrollSubmitSuccess = () => {
    const emp = queue[qi];
    setSubmitWizardOpen(false);
    if (emp) optimisticMove('unsubmitted', 'submitted', emp.employeeID);
    loadAllBuckets(); // background reconcile
    backToBoard();
    const empName = emp ? displayName(emp.name) : 'the employee';
    setCMConfig({
      variant: 'success',
      icon: <CheckCircle size={22} />,
      title: `Submitted for ${empName}`,
      subtitle: `Recorded as Submitted by Payroll${userName ? ` · ${userName}` : ''}. The timesheet moved to ${BMAP.submitted.label}.`,
    });
  };

  // 409 primary action: reload the drill-down in place — the same read now finds
  // the employee's own submission, so the console shows the submitted week.
  const handleConflictOpenSubmittedWeek = () => {
    setSubmitWizardOpen(false);
    loadAllBuckets();
    const emp = queue[qi];
    if (emp) loadReview(emp, payPeriodID);
  };

  // 409 secondary action: back to the (refreshed) board.
  const handleConflictBackToBoard = () => {
    setSubmitWizardOpen(false);
    loadAllBuckets();
    backToBoard();
  };

  // Edit as payroll → the M2 editor page. The intent hands the editor its
  // employee (the pay period rides on the shared context payPeriodID).
  const handleEditAsPayroll = () => {
    const emp = queue[qi];
    if (!emp) return;
    setPayrollEditIntent({
      employeeID: emp.employeeID,
      altEmployeeID: emp.altEmployeeID,
      name: emp.name,
    });
    setPage(Page.PayrollTimesheetEdit);
  };

  // ── Header week write-through (used by the console's internal week nav) ──────
  const onSelectWeek = (idx: number) => {
    const pp = payPeriods[idx];
    if (!pp) return;
    setWeek(pp.WeekNumber);
    setYear(String(pp.AccountingYear));
    setPayPeriodID(pp.PayPeriodID);
    setStartDate(getPayPeriodDate(new Date(pp.StartDate)));
    setEndDate(getPayPeriodDate(new Date(pp.EndDate)));
  };

  // Employee search lives in the global header now (components/v2/GlobalEmployeeSearch).
  // It hands an employee over as a ReviewIntent, which openEmployeeIntent below resolves
  // against the loaded buckets — the same bucket sweep, queue build and synthesized
  // single-item fallback the page-local search used to do for itself.

  // ── Derived (board) ─────────────────────────────────────────────────────────
  const listGroups = fgroups(selectedBucket);
  const listTotal = listCount(selectedBucket);
  const isFiltering = textFilter.trim() !== '';
  const filtersActive = locationFilter !== 'all' || supervisorFilter !== 'all' || isFiltering || flagsActive;
  // Collapse rules: single crew → always open (no chevron); multiple crews →
  // collapsed by default, header toggles; a text filter force-expands every match.
  const multiSup = listGroups.length > 1;
  const collapsible = multiSup && !isFiltering;
  const isExpanded = (supId: number) => isFiltering || !multiSup || expanded.has(supId);
  const selMeta = BMAP[selectedBucket];
  // Stage grouping vocabulary (design §2.2/§2.3): the 1st Approval stage lists
  // approvers and their queues; every other stage lists supervisors and their crews.
  const groupByApprover = selMeta.groupWord === 'approver';
  const allGroupsLabel =
    groupByApprover ? 'All approvers'
    : selMeta.groupWord === 'required super' ? 'All required supers'
    : 'All supervisors';
  // Rows on screen vs. distinct timesheets — the gap is employees who owe more than
  // one approver, and it exists only in the 1st Approval stage.
  const multiApproverEmployees = multiGroupCount(listGroups);
  const locNote = locationFilter === 'all' ? '' : ` · ${locationName(locationFilter)}`;
  // NPM payroll bar gates: Print All matches the legacy button (coarse buckets);
  // the summary download matches the API's exportPayroll 403 gate (admin implies it).
  const canPrintAll = config.printAllTimesheets && (userRole.admin || userRole.payroll);
  // Bucket-level Approve All operates on the crews CURRENTLY IN VIEW, so every filter
  // (location, supervisor, text, flags) narrows it. v1 had the same behavior via its search
  // box but never said so; here the ready/skipped counts are on the bar.
  const bulkPartition = useMemo(
    () => partitionForBulk(canBulkApprove ? listGroups.flatMap((g) => g.employees ?? []) : []),
    [canBulkApprove, listGroups, partitionForBulk]
  );
  const canDownloadSummary = !!config.showPayrollApprovedSummaryReport && can(userRole, 'exportPayroll');
  // Vista Post Verification: same tenant-flag + exportPayroll shape as the summary beside it.
  const canDownloadPostVerification =
    !!config.showVistaPostVerificationReport && can(userRole, 'exportPayroll');
  // Wallcraft Spectrum export: tenant flag + the same ExportPayroll gate the summary report
  // uses (and the gate the API's /admin/wallcraft/spectrum routes actually enforce).
  const canSpectrumExport = !!config.showSpectrumExport && can(userRole, 'exportPayroll');
  // Wallcraft Payroll Summary (whole week, as pushed to Spectrum) — same ExportPayroll gate.
  const canWallcraftSummary = !!config.showWallcraftPayrollSummary && can(userRole, 'exportPayroll');
  // WCDP IAS export + recorded Paycor file (TAS-682): tenant flag + ExportPayroll, the gate
  // the API's /admin/wcdp/iasExport routes enforce.
  const canWcdpExport = !!config.showWcdpBatchExport && can(userRole, 'exportPayroll');

  // Changed-since-push callouts. Refetched when the Payroll Approved bucket is shown, the
  // week changes, the buckets reload (every approve / unlock / payroll edit path ends in
  // loadAllBuckets -> allSupervisorData) and after a push. Advisory: a failure only means
  // no chips — it never touches the board.
  useEffect(() => { setSpectrumDrift([]); }, [currentPayPeriodID]);
  useEffect(() => {
    if (!canSpectrumExport || selectedBucket !== 'payrollApproved' || !currentPayPeriodID || !bucketsLoaded) return;
    let alive = true;
    getSpectrumChanges(currentPayPeriodID)
      .then((res) => { if (alive) setSpectrumDrift(res.employees); })
      .catch((err) => {
        console.warn('Spectrum change check failed; no drift chips shown.', err);
        if (alive) setSpectrumDrift([]);
      });
    return () => { alive = false; };
  }, [canSpectrumExport, selectedBucket, currentPayPeriodID, bucketsLoaded, allSupervisorData, driftNonce]);
  const driftByEmployee = useMemo(
    () => new Map(spectrumDrift.map((d) => [d.employeeID, d])),
    [spectrumDrift]);

  // Spectrum export status — the Wallcraft twin of the Sage status below. Not gated on the
  // selected bucket: the Payroll Approved stage tile shows exported / not exported from any stage.
  useEffect(() => { setSpectrumStatus(null); }, [currentPayPeriodID]);
  useEffect(() => {
    if (!canSpectrumExport || !currentPayPeriodID || !bucketsLoaded) return;
    let alive = true;
    getSpectrumExportStatus(currentPayPeriodID)
      .then((res) => { if (alive) setSpectrumStatus(res); })
      .catch((err) => {
        console.warn('Spectrum export status check failed; no export chips shown.', err);
        if (alive) setSpectrumStatus(null);
      });
    return () => { alive = false; };
  }, [canSpectrumExport, currentPayPeriodID, bucketsLoaded, allSupervisorData, driftNonce]);
  const spectrumExportedByEmp = useMemo<Map<number, SpectrumExportedInfo> | null>(() => {
    if (!spectrumStatus || spectrumStatus.payPeriodID !== currentPayPeriodID) return null;
    return new Map(spectrumStatus.employees.map((x) => [x.employeeID, x]));
  }, [spectrumStatus, currentPayPeriodID]);
  const spectrumActive = canSpectrumExport && selectedBucket === 'payrollApproved' && spectrumExportedByEmp !== null;
  /**
   * Spectrum never records an employee with no rows, so "nothing to export" is derived: a
   * current snapshot with no work, sick or piece. Stale / missing snapshot = unknown = false.
   */
  const spectrumNothingToExport = (e: SupervisorsEmployee): boolean => {
    const s = e.summary;
    return !!s && !e.snapshotStale && s.totalWorkMinutes === 0 && s.sickMinutes === 0 && s.taskClaimedAmount === 0;
  };
  /** Filter class: pushed or nothing-to-export both count as "exported" — neither is to-export. */
  const isSpectrumExported = (e: SupervisorsEmployee): boolean =>
    (spectrumExportedByEmp?.has(e.employeeID) ?? false) || spectrumNothingToExport(e);
  const driftBySupervisor = useMemo(() => {
    const m = new Map<number, SpectrumDriftEmployee[]>();
    spectrumDrift.forEach((d) => m.set(d.supervisorID, [...(m.get(d.supervisorID) ?? []), d]));
    return m;
  }, [spectrumDrift]);

  // 2020PH/NV Sage export record (TAS-180). Same gate as the Sage export bar. Refetched like
  // the Spectrum drift above — bucket shown, week change, bucket reload — plus after every
  // push attempt (sageStatusNonce). Advisory: a failure only means no chips / no filter.
  const canSage = !!config.showSageExport && (can(userRole, 'exportPayroll') || can(userRole, 'exportAccounting'));
  useEffect(() => { setSageStatus(null); }, [currentPayPeriodID]);
  useEffect(() => {
    // Not gated on the selected bucket: the Payroll Approved stage tile shows exported / not
    // exported counts from whichever stage is open.
    if (!canSage || !currentPayPeriodID || !bucketsLoaded) return;
    let alive = true;
    getSageExportStatus(currentPayPeriodID)
      .then((res) => { if (alive) setSageStatus(res); })
      .catch((err) => {
        console.warn('Sage export status check failed; no export chips shown.', err);
        if (alive) setSageStatus(null);
      })
      // A failure settles too — the API's own already-exported guard still applies to a push.
      .finally(() => { if (alive) setSageStatusSettledPP(currentPayPeriodID); });
    return () => { alive = false; };
  }, [canSage, currentPayPeriodID, bucketsLoaded, allSupervisorData, sageStatusNonce]);
  // null while unknown — chips, the summary and the filter all stand down rather than claim
  // "Not exported" for a week whose record never loaded.
  const sageExportedByEmp = useMemo<Map<number, SageExportedInfo> | null>(() => {
    if (!sageStatus || sageStatus.payPeriodID !== currentPayPeriodID) return null;
    return new Map((sageStatus.employees ?? []).map((x) => [x.employeeID, x]));
  }, [sageStatus, currentPayPeriodID]);
  const sageActive = canSage && selectedBucket === 'payrollApproved' && sageExportedByEmp !== null;
  const sageStatusReady = sageStatusSettledPP === currentPayPeriodID;

  // WCDP IAS export record (TAS-682) — the Sage effect above, Payroll Approved only (WCDP has
  // no tile or filter). Refetched on week change, bucket reload and after every export /
  // Paycor attempt (wcdpStatusNonce). Advisory: a failure only means no chips / no counts.
  useEffect(() => { setWcdpStatus(null); }, [currentPayPeriodID]);
  useEffect(() => {
    if (!canWcdpExport || selectedBucket !== 'payrollApproved' || !currentPayPeriodID || !bucketsLoaded) return;
    let alive = true;
    getWcdpIasExportStatus(currentPayPeriodID)
      .then((res) => { if (alive) setWcdpStatus(res); })
      .catch((err) => {
        console.warn('IAS export status check failed; no export chips shown.', err);
        if (alive) setWcdpStatus(null);
      });
    return () => { alive = false; };
  }, [canWcdpExport, selectedBucket, currentPayPeriodID, bucketsLoaded, allSupervisorData, wcdpStatusNonce]);
  const wcdpExportedByEmp = useMemo<WcdpExportMap | null>(() => {
    if (!wcdpStatus || wcdpStatus.payPeriodID !== currentPayPeriodID) return null;
    return new Map((wcdpStatus.employees ?? []).map((x) => [x.employeeID, x]));
  }, [wcdpStatus, currentPayPeriodID]);
  // The chip slot is reserved whenever the export is on in this bucket — loaded or not — so
  // a chip arriving later never moves the row's other flags.
  const wcdpSlotOn = canWcdpExport && selectedBucket === 'payrollApproved';
  const wcdpActive = wcdpSlotOn && wcdpExportedByEmp !== null;
  /** Filter class: noRows, changed and unconfirmed all count as "exported" — none is to-export. */
  const isSageExported = useCallback(
    (employeeID: number) => sageExportedByEmp?.has(employeeID) ?? false,
    [sageExportedByEmp]);
  // Header roll-up over the UNFILTERED approved set in view (the same set the picker gets),
  // never the Sage display filter.
  const sageSummary = useMemo(() => {
    if (!sageExportedByEmp) return null;
    const ids = Array.from(new Set(sageItems.map((s) => s.employeeID)));
    let exported = 0; let nothing = 0; let changed = 0; let unconfirmed = 0;
    ids.forEach((id) => {
      const x = sageExportedByEmp.get(id);
      if (!x) return;
      if (x.noRows) { nothing += 1; return; }
      exported += 1;
      if (!x.confirmed) unconfirmed += 1;
      else if (x.changedSinceExport) changed += 1;
    });
    const gone = (sageStatus?.employees ?? []).filter((x) => x.stillApproved === false);
    return {
      approved: ids.length, exported, nothing, changed, unconfirmed,
      toExport: ids.length - exported - nothing,
      gone,
    };
  }, [sageExportedByEmp, sageItems, sageStatus]);
  // Payroll Approved stage tile: exported vs not exported over the SAME set as the tile's own
  // count (location / supervisor filters, not the text filter). "Nothing to export" (noRows)
  // employees are in neither number.
  const sageTile = useMemo(() => {
    if (!sageExportedByEmp) return null;
    const ids = new Set<number>();
    filteredGroups('payrollApproved').forEach((g) => (g.employees ?? []).forEach((e) => ids.add(e.employeeID)));
    let exported = 0; let notExported = 0;
    ids.forEach((id) => {
      const x = sageExportedByEmp.get(id);
      if (!x) notExported += 1;
      else if (!x.noRows) exported += 1;
    });
    return { exported, notExported };
  }, [sageExportedByEmp, filteredGroups]);
  // The Sage filter is DISPLAY-only: it trims the rendered rows and nothing else. listGroups /
  // fgroups (bucket counts, crew queues, "Review N one-by-one", sageItems) never see it.
  // Crews left with no visible employee drop out of the list.
  // Wallcraft and WCDP share the same filter state (one tenant only ever has one export on).
  const sageFilterOn = (sageActive || spectrumActive || wcdpActive) && sageFilter !== 'all';
  const sageVisibleGroups = useMemo(() => {
    const all = listGroups.map((g) => ({ g, rows: g.employees ?? [] }));
    if (!sageFilterOn) return all;
    const want = sageFilter === 'exported';
    // WCDP (TAS-682): any batch-export record counts — changed / unconfirmed / nothing-to-export
    // included, the same set the crew header's "X of Y exported" counts.
    const exported = (e: SupervisorsEmployee) => (sageActive
      ? isSageExported(e.employeeID)
      : wcdpActive ? (wcdpExportedByEmp?.has(e.employeeID) ?? false) : isSpectrumExported(e));
    return all
      .map(({ g, rows }) => ({ g, rows: rows.filter((e) => exported(e) === want) }))
      .filter(({ rows }) => rows.length > 0);
    // isSpectrumExported is re-created each render; spectrumExportedByEmp is what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listGroups, sageFilterOn, sageFilter, isSageExported, sageActive, spectrumExportedByEmp, wcdpActive, wcdpExportedByEmp]);
  // Spectrum roll-ups: the export bar (approved set in view) and the Payroll Approved tile
  // (same set as the tile's own count). Nothing-to-export employees are in neither number.
  const spectrumSummary = useMemo(() => {
    if (!spectrumExportedByEmp) return null;
    const seen = new Set<number>();
    let approved = 0; let exported = 0; let nothing = 0; let changed = 0;
    listGroups.forEach((g) => (g.employees ?? []).forEach((e) => {
      if (seen.has(e.employeeID)) return;
      seen.add(e.employeeID);
      approved += 1;
      if (spectrumExportedByEmp.has(e.employeeID)) {
        exported += 1;
        if (driftByEmployee.has(e.employeeID)) changed += 1;
      } else if (spectrumNothingToExport(e)) nothing += 1;
    }));
    const gone = spectrumStatus?.employees.filter((x) => !x.stillApproved) ?? [];
    return { approved, exported, nothing, changed, toExport: approved - exported - nothing, gone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spectrumExportedByEmp, listGroups, driftByEmployee, spectrumStatus]);
  const spectrumTile = useMemo(() => {
    if (!spectrumExportedByEmp) return null;
    const emps = new Map<number, SupervisorsEmployee>();
    filteredGroups('payrollApproved').forEach((g) => (g.employees ?? []).forEach((e) => emps.set(e.employeeID, e)));
    let exported = 0; let notExported = 0;
    emps.forEach((e) => {
      if (spectrumExportedByEmp.has(e.employeeID)) exported += 1;
      else if (!spectrumNothingToExport(e)) notExported += 1;
    });
    return { exported, notExported };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spectrumExportedByEmp, filteredGroups]);
  // WCDP batch-export roll-ups (TAS-682), counted exactly like Sage's: the bar over the approved
  // set in view, the tile over the tile's own set. Nothing-to-export (noRows) employees are in
  // neither "exported" nor "to export".
  const wcdpSummary = useMemo(() => {
    if (!wcdpActive || !wcdpExportedByEmp) return null;
    const ids = new Set<number>();
    listGroups.forEach((g) => (g.employees ?? []).forEach((e) => ids.add(e.employeeID)));
    let exported = 0; let nothing = 0; let changed = 0; let unconfirmed = 0;
    ids.forEach((id) => {
      const x = wcdpExportedByEmp.get(id);
      if (!x) return;
      if (x.noRows) { nothing += 1; return; }
      exported += 1;
      if (x.confirmed === false) unconfirmed += 1;
      else if (x.changedSinceExport) changed += 1;
    });
    const gone = (wcdpStatus?.employees ?? []).filter((x) => x.stillApproved === false).length;
    return { approved: ids.size, exported, nothing, changed, unconfirmed, toExport: ids.size - exported - nothing, gone };
  }, [wcdpActive, wcdpExportedByEmp, listGroups, wcdpStatus]);
  const wcdpTile = useMemo(() => {
    if (!wcdpActive || !wcdpExportedByEmp) return null;
    const ids = new Set<number>();
    filteredGroups('payrollApproved').forEach((g) => (g.employees ?? []).forEach((e) => ids.add(e.employeeID)));
    let exported = 0; let notExported = 0;
    ids.forEach((id) => {
      const x = wcdpExportedByEmp.get(id);
      if (!x) notExported += 1;
      else if (!x.noRows) exported += 1;
    });
    return { exported, notExported };
  }, [wcdpActive, wcdpExportedByEmp, filteredGroups]);
  const exportTile = sageTile
    ? { ...sageTile, erp: 'Sage' }
    : spectrumTile ? { ...spectrumTile, erp: 'Spectrum' }
      : wcdpTile ? { ...wcdpTile, erp: 'Batch' } : null;
  // NPM Vista export. Gated on `exportPayroll` to match what the API actually enforces —
  // the v1 buttons check raw `admin || payroll || hr` and hand an hr-only user a 403.
  const canVistaExport = config.vistaBatchExport === true && can(userRole, 'exportPayroll');
  const selectableLocations = useMemo(
    () => locations.filter((l) => !l.testLocation),
    [locations]
  );

  // Unsubmitted carries no snapshot by design, so the strip collapses to today's
  // identity row there (and wherever a tenant switches the strip off entirely).
  const identityOnly = stripOff(selectedBucket);
  // Bucket-wide staleness note: counted over the rows actually on screen.
  const staleCount = listGroups
    .reduce((n, g) => n + (g.employees ?? []).filter((e) => e.snapshotStale).length, 0);

  // ── Flag facet counts / held-out rows ───────────────────────────────────────
  // Both are measured over the PRE-facet set (textGroups), so the counts are the
  // facet's own shape rather than a reflection of what is already selected.
  const preFacetEmployees = useMemo(
    () => {
      if (identityOnly) return [];
      // One row per employee: a timesheet listed under several approvers (1st Approval
      // stage, WCDP Modified Duty) is still one timesheet for the facet counts.
      const seen = new Set<number>();
      return textGroups(selectedBucket).flatMap((g) => g.employees ?? []).filter((e) => {
        if (seen.has(e.employeeID)) return false;
        seen.add(e.employeeID);
        return true;
      });
    },
    [identityOnly, textGroups, selectedBucket]
  );
  const facetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    facetDefs.forEach((f) => {
      counts[f.k] = preFacetEmployees
        .filter((e) => (f.rowTest ? f.rowTest(e) : isUsable(e) && f.test(e.summary as TimesheetSummary))).length;
    });
    return counts;
  }, [facetDefs, preFacetEmployees]);
  // Rows a flag filter cannot judge. Counted and stated in the list note — never silently
  // dropped. A row is held out only when a SELECTED facet needed a snapshot the row does
  // not have; if every selected facet is row-level, a non-match is a real "no", not an
  // unknown, and nothing is held out.
  const snapshotFacetSelected = facetDefs.some((f) => flagFilter.has(f.k) && !f.rowTest);
  const heldOutCount = flagsActive && snapshotFacetSelected
    ? preFacetEmployees.filter((e) => !isUsable(e) && !matchesFlags(e)).length
    : 0;
  const showFlagBar = !identityOnly && !bucketsLoading && facetDefs.length > 0;

  // ── Derived (queue) ─────────────────────────────────────────────────────────
  const originMeta = origin ? BMAP[origin.bucket] : null;
  const total = queue.length;
  const approvedCount = queue.filter((q) => approvedIDs.has(q.employeeID)).length;
  const remaining = total - approvedCount;
  const currentEntry = queue[qi];
  // Per-bucket action gating (spec §15.3.15): the approve action requires ApproveTimesheets
  // for the manager/supervisor (submitted) bucket and PayrollApproveTimesheets for the
  // Payroll Pending (supervisorApproved) bucket. Admin implies both.
  const canApproveOrigin = !!origin
    && (origin.bucket === 'submitted' || origin.bucket === 'supervisorApproved')
    && canApproveStage(userRole, origin.bucket);
  const isComplete = canApproveOrigin && total > 0 && remaining === 0;
  // Change Supervisor visibility. v1's status pair (Submitted + Supervisor Approved)
  // and v1's placement (one timesheet at a time, in the header action row), but gated
  // on the dedicated ReassignTimesheetSupervisor permission the API enforces rather
  // than v1's coarse payroll/admin booleans. Hidden rather than shown disabled.
  // supervisorID is 0 only for a queue entry synthesized outside a crew group (the
  // global-search fallback); with no "from" supervisor to name, the button stays hidden.
  const supBucket = actualStatus ?? origin?.bucket;
  const canChangeSupervisor = can(userRole, 'reassignTimesheetSupervisor')
    && (supBucket === 'submitted' || supBucket === 'supervisorApproved')
    && !!timesheetData
    && (currentEntry?.supervisorID ?? 0) > 0;

  // Supervisor picker options. The employee number goes IN the label — FloatMenuButton
  // filters on the label, so typing either the name or the EE# finds the row. Under the
  // EE#-first flag the label and the ordering come from supervisorPickerOptions; without
  // it the label stays "Last, First · #AltID" in the API's own order, as before.
  // The current supervisor is omitted: reassigning to them is a no-op, and the intro
  // line already names them.
  const supLabel = useCallback(
    (s: { name: { first: string; last: string }; altEmployeeID?: string }) =>
      supervisorNumbersOn()
        ? supervisorOptionLabel(`${s.name.last}, ${s.name.first}`, s.altEmployeeID)
        : `${s.name.last}, ${s.name.first}${s.altEmployeeID ? ` · #${s.altEmployeeID}` : ''}`,
    []
  );
  const supMenuOptions = useMemo(
    () => sortSupervisorOptions(
      supOptions
        .filter((s) => s.employeeID !== currentEntry?.supervisorID)
        .map((s) => ({
          value: String(s.employeeID),
          label: supLabel(s),
          name: `${s.name.last}, ${s.name.first}`,
          altEmployeeID: s.altEmployeeID,
        }))
    ).map(({ value, label }) => ({ value, label })),
    [supOptions, currentEntry, supLabel]
  );
  const supPicked = supPick == null ? null : supOptions.find((s) => s.employeeID === supPick) ?? null;

  // ── RENDER: the metric strip ────────────────────────────────────────────────
  // The dash contract (design §5): a value means computed, EMPTY means nothing to
  // report, and an em dash means not known. A missing or stale snapshot dashes.

  const dashCell = (
    <>
      <span className="ss-v ss-dash">—</span>
      <span className="ss-sub"><span className="ss-dash">—</span></span>
    </>
  );

  const hoursCell = (e: SupervisorsEmployee) => {
    const s = e.summary;
    if (!s || e.snapshotStale) return dashCell;
    const ot = s.overtimeMinutes;
    const dt = s.doubleTimeMinutes;
    // The premium line renders only when there IS a premium — a zero-OT row is two
    // lines, which is what makes an OT row findable by scanning.
    const premTip = [
      ot > 0 ? `Overtime ${fmtHrs(ot)} hrs` : '',
      dt > 0 ? `Double time ${fmtHrs(dt)} hrs` : '',
    ].filter(Boolean).join(' · ');
    const pc = s.taskMinutes;
    const hr = s.hourlyMinutes;
    const sk = s.sickMinutes;
    // Wallcraft production totals (B4): this tenant's supervisors never see "piece"
    // language — to them the claim rows ARE production. Label only; the split is
    // unchanged, and every other tenant reads exactly what it read before.
    const claimWord = productionPayOn ? 'Production' : 'Piece';
    const barTip = `${claimWord} ${fmtHrs(pc)} hrs · Hourly ${fmtHrs(hr)} hrs${sk > 0 ? ` · Sick ${fmtHrs(sk)} hrs` : ''}`;
    return (
      <>
        <span className="ss-v">{fmtHrs(s.totalWorkMinutes)}<span className="u">hrs</span></span>
        {(ot > 0 || dt > 0) && (
          <span className="ss-sub" title={premTip}>
            {ot > 0 && <span className="ss-ot">OT {fmtHrs(ot)}</span>}
            {dt > 0 && <span className="ss-dt"><span className="sep">·</span>DT {fmtHrs(dt)}</span>}
          </span>
        )}
        {pc + hr + sk > 0 && (
          <span className="ss-bar" title={barTip}>
            {pc > 0 && <i className="pc" style={{ flex: pc }} />}
            {hr > 0 && <i className="hr" style={{ flex: hr }} />}
            {sk > 0 && <i className="sk" style={{ flex: sk }} />}
          </span>
        )}
      </>
    );
  };

  const moneyCell = (e: SupervisorsEmployee) => {
    const s = e.summary;
    if (!s || e.snapshotStale) return dashCell;
    // Nothing claimed at all — no piece minutes, no lines, no dollars. The slot stays
    // EMPTY: an all-zero claim is noise on an hourly week and a dash would read
    // "unknown". A partial zero (lines logged but $0.00 claimed) still renders — that
    // is a finding, not an absence.
    if (s.taskMinutes === 0 && s.taskCount === 0 && s.taskClaimedAmount === 0) return null;
    return (
      <>
        {/* Wallcraft production totals (B4): "Total $" / "account code" wording, since
            the dollars on this tenant's rows are supervisor-entered production totals
            rather than employee piece claims. Label only — the figures are the same. */}
        <span className="ss-v">
          <span className="cl">{productionPayOn ? 'total $' : 'claimed'}</span>${fmtMoney(s.taskClaimedAmount)}
        </span>
        <span className="ss-sub">
          {/* "task"/"tasks" is client vocabulary, not a fixed label: 2020PH calls these
              pieces. Company-scoped keys (2020ph_TaskCountSingular) win over the plain
              ones via languagePrefix, so every other tenant is unaffected. Two keys rather
              than one + "s" because the plural is not suffix-formed in every language —
              Spanish already needs tarea/tareas and pieza/piezas. */}
          {s.taskCount}{' '}
          {productionPayOn
            ? `account code${s.taskCount === 1 ? '' : 's'}`
            : translator.translate(s.taskCount === 1 ? 'TaskCountSingular' : 'TaskCountPlural')}
          {/* Gated on showAvgHourlyRate (cap.avgRate): a tenant with derived $/hour figures
              switched off elsewhere must not meet one here. The claimed dollars and the task
              count are unaffected — only the derived rate goes.
              Also omitted without piece minutes, where it is undefined rather than unknown. */}
          {cap.avgRate && s.taskMinutes > 0 && (
            <><span className="sep">·</span>${fmtMoney(s.taskClaimedAmount / (s.taskMinutes / 60))}/hr</>
          )}
        </span>
      </>
    );
  };

  const flagsCell = (e: SupervisorsEmployee) => {
    const s = e.summary;
    // The blocking flag is computed live and does NOT depend on a snapshot, so it renders
    // even on a row that has none — which, in Payroll Pending, is most of them.
    const blocks = cap.sickRate && needsSickRate(e);
    const blockingPill = blocks ? (
      <span className="pill warn" title="A sick claim has minutes but no hourly rate — blocks payroll approval">
        <AlertCircle />Sick<span className="lbl"> rate</span>
      </span>
    ) : null;
    // TAS-679 (WCDP): Modified Duty is a live row field, not a snapshot one, so like the
    // blocking flag it renders on rows with no (or a stale) snapshot. Never .opt — it is
    // why HR's approval is required, so it must not shed on narrow rows.
    const mdChip = cap.md && e.hasModifiedDuty ? (
      <span key="md" className="ss-chip mdflag" title="On Modified Duty during this pay week — HR approval is required in addition to the supervisor">
        Mod duty
      </span>
    ) : null;
    const blocking = mdChip || blockingPill ? <>{mdChip}{blockingPill}</> : null;
    if (!s) return blocking;
    // A stale snapshot's booleans are as superseded as its numbers, so the informational
    // chips go with them. The blocking flag is the one exception: a stale false positive
    // costs a reviewer one glance, a false negative silently blocks payroll.
    if (e.snapshotStale) return blocking;
    const chips: React.ReactElement[] = [];
    if (cap.meal && s.mealPenaltyDayCount > 0) {
      chips.push(
        <span key="meal" className="ss-chip meal opt" title={`${s.mealPenaltyDayCount} day${s.mealPenaltyDayCount === 1 ? '' : 's'} with a meal-penalty exception`}>
          {s.mealPenaltyDayCount} meal
        </span>
      );
    }
    if (cap.misc && s.hasMiscTasks) {
      chips.push(<span key="misc" className="ss-chip misc opt" title="Miscellaneous task claims in this period">Misc</span>);
    }
    if (cap.cs && s.hasCustomerService) {
      chips.push(<span key="cs" className="ss-chip csw opt" title="Customer service / warranty work in this period">C/S</span>);
    }
    if (cap.trueup && s.hasMinWageTrueUp) {
      chips.push(<span key="trueup" className="ss-chip trueup opt" title="Minimum wage true-up computed for this period">True-up</span>);
    }
    if (cap.supp && s.hasSupplementalPay) {
      chips.push(<span key="supp" className="ss-chip supp opt" title="Supplemental pay computed for this period">Supp</span>);
    }
    if (cap.pw && s.hasPrevailingWage) {
      chips.push(<span key="pw" className="ss-chip pwflag opt" title="Prevailing wage work in this period">PW</span>);
    }
    if (cap.holiday && s.hasHoliday) {
      chips.push(<span key="hol" className="ss-chip hol opt" title="Period includes holiday pay">Hol</span>);
    }
    if (s.hasTimeOff) {
      chips.push(<span key="pto" className="ss-chip pto opt" title="Approved time off overlaps this period">PTO</span>);
    }
    if (s.hasSickTime && !blocks) {
      chips.push(<span key="sick" className="ss-chip sick opt" title={`Sick time claimed: ${fmtHrs(s.sickMinutes)} hrs`}>Sick</span>);
    }
    return (
      <>
        {chips}
        {chips.length > 0 && (
          <span className="ss-chip more" title={`${chips.length} more flag${chips.length === 1 ? '' : 's'} — widen to see them`}>
            +{chips.length}
          </span>
        )}
        {blocking}
      </>
    );
  };

  /** Crew roll-up — sums only usable rows over the DISPLAYED (filtered) employees, and
   *  admits the shortfall rather than quietly under-reporting. */
  const rollupLine = (g: Supervisor) => {
    if (identityOnly) return null; // unsubmitted: header stays a single identity line
    const emps = g.employees ?? [];
    const ok = emps.filter(isUsable);
    // Counted over EVERY employee, not just the snapshot-usable ones: the blocking flag is
    // live, so a crew whose rows have no snapshot still reports its blocked count.
    const blocked = cap.sickRate ? emps.filter(needsSickRate).length : 0;
    // No usable snapshot anywhere in the crew — the normal state in Payroll Pending, since
    // supervisor approval does not stamp one. There are no totals to show, but the blocking
    // count is live and is exactly what payroll needs here, so it still reports.
    if (ok.length === 0) {
      return (
        <span className="ss-rollup none">
          No snapshot data for this crew yet
          {blocked > 0 && (
            <>
              <span className="sep">·</span>
              <span className="blocked"><AlertCircle />{blocked} need{blocked === 1 ? 's' : ''} sick rate</span>
            </>
          )}
        </span>
      );
    }
    const mins = ok.reduce((a, e) => a + (e.summary?.totalWorkMinutes ?? 0), 0);
    const amt = ok.reduce((a, e) => a + (e.summary?.taskClaimedAmount ?? 0), 0);
    const missing = emps.length - ok.length;
    return (
      <span className="ss-rollup">
        <span><span className="lab">crew</span>{fmtHrs(mins)} hrs</span>
        {cap.rollupClaimed && (
          <span className="ss-rmoney">
            <span className="sep">·</span>
            <span className="lab">{productionPayOn ? 'total $' : 'claimed'}</span>${fmtMoney(amt)}
          </span>
        )}
        {/* The denominator appears only when the sum is incomplete — its presence is
            the signal, and the hover gives the reason. */}
        {missing > 0 && (
          <span
            className="ss-denom"
            title={`${missing} timesheet${missing === 1 ? '' : 's'} in this crew ${missing === 1 ? 'has' : 'have'} no usable snapshot and ${missing === 1 ? 'is' : 'are'} excluded from the totals`}
          >
            {ok.length} of {emps.length}
          </span>
        )}
        {blocked > 0 && (
          <>
            <span className="sep">·</span>
            <span className="blocked"><AlertCircle />{blocked} need{blocked === 1 ? 's' : ''} sick rate</span>
          </>
        )}
      </span>
    );
  };

  /** "1 of 3 approvals" — first-level slots satisfied on this timesheet (manager +
   *  approval group; payroll and required-super belong to later stages). Amber while
   *  outstanding, green once complete. Only the 1st Approval stage has the counts. */
  const slotCountChip = (e: SupervisorsEmployee) => {
    if (!groupByApprover) return null;
    const total = e.approvalsTotal;
    const done = e.approvalsDone;
    if (typeof total !== 'number' || total <= 0 || typeof done !== 'number') return null;
    const complete = done >= total;
    return (
      <span
        className={`tr-slotcount${complete ? ' ok' : ''}`}
        title={complete
          ? 'Every first-level approval on this timesheet is complete'
          : `${total - done} first-level approval${total - done === 1 ? '' : 's'} still outstanding`}
      >
        {done} of {total} approvals
      </span>
    );
  };

  /** TAS-679 (WCDP Modified Duty): "1 of 2 · waiting on Adelina Estrada" on a Submitted row
   *  with 2+ required supervisor slots. Null for every row without those fields. */
  const superSlotChip = (e: SupervisorsEmployee) => {
    const chip = boardSuperSlotChip(e);
    if (!chip) return null;
    return (
      <span className={`tr-slotcount tr-supslots${chip.complete ? ' ok' : ''}`} title={chip.title}>
        {chip.text}
      </span>
    );
  };

  /** Payroll Approved row chip from the Sage export record (only rendered while it loaded). */
  const sageExportChip = (employeeID: number) => {
    const x = sageExportedByEmp?.get(employeeID);
    if (!x) {
      return <span className="ss-chip tr-sage-off" title="Not yet exported to Sage this pay period">Not exported</span>;
    }
    if (x.noRows) {
      return <span className="ss-chip tr-sage-off" title={sageExportFacts(x)}>Nothing to export</span>;
    }
    if (!x.confirmed) {
      return (
        <span className="ss-chip tr-sage-warn" title={sageExportFacts(x)}>
          <AlertTriangle />Unconfirmed – check Sage
        </span>
      );
    }
    if (x.changedSinceExport) {
      const why = (x.changeReasons ?? []).map((r) => SAGE_CHANGE_REASON_TEXT[r] ?? r).join('; ');
      return (
        <span className="ss-chip tr-sage-warn" title={`${why ? `Changed: ${why}\n` : ''}${sageExportFacts(x)}`}>
          <AlertTriangle />Changed since export
        </span>
      );
    }
    return (
      <span className="ss-chip tr-sage-ok" title={sageExportFacts(x)}>
        Exported {fmtSageExportTime(x.exportedAt).split(' ')[0]}
      </span>
    );
  };

  /**
   * Payroll Approved row chip from the Spectrum push record (Wallcraft). "Changed since push"
   * is the existing drift chip, so the caller skips this one when that is showing.
   */
  const spectrumExportChip = (e: SupervisorsEmployee) => {
    const x = spectrumExportedByEmp?.get(e.employeeID);
    if (!x) {
      return spectrumNothingToExport(e)
        ? <span className="ss-chip tr-sage-off" title="No hours or pieces — nothing to push to Spectrum">Nothing to export</span>
        : <span className="ss-chip tr-sage-off" title="Not yet pushed to Spectrum this pay period">Not exported</span>;
    }
    return (
      <span className="ss-chip tr-sage-ok" title={spectrumExportFacts(x)}>
        Exported {fmtSageExportTime(x.pushedAt).split(' ')[0]}
      </span>
    );
  };

  /** WCDP Payroll Approved row chip (TAS-682): nothing until exported; no tooltips. */
  const wcdpExportChip = (employeeID: number) => {
    const x = wcdpActive ? wcdpExportedByEmp?.get(employeeID) : undefined;
    const kind = wcdpChipKind(x);
    const text = wcdpChipText(x);
    let chip: React.ReactNode = null;
    if (text) {
      chip = kind === 'exported'
        ? <span className="ss-chip tr-sage-ok tr-wcdpchip">{text}</span>
        : <span className="ss-chip tr-sage-warn tr-wcdpchip"><AlertTriangle />{text}</span>;
    }
    return <span className="tr-wcdpslot">{chip}</span>;
  };

  const employeeRow = (e: SupervisorsEmployee) => {
    const hue = hashHue(`${e.name.first} ${e.name.last}`);
    // M1-B3: `active === false` arrives only on the Unsubmitted-bucket reads
    // (TAS-391) — terminated employee with pending work. The avatar greys and
    // the outlined Inactive badge joins .tr-eflags before the chevron, per the
    // accepted design v2 (shot 01). Flag off (or `active` absent) renders
    // exactly today's row.
    const inactive = payrollEditingOn && e.active === false;
    const avatar = (
      <span
        className={`tr-eav${inactive ? ' off' : ''}`}
        style={inactive ? undefined : { background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
      >
        {initials(e.name.first, e.name.last)}
      </span>
    );
    const who = (
      <span className="tr-ewho">
        <span className="tr-en">{e.name.first} {e.name.last}</span>
        <span className="tr-eid">#{e.altEmployeeID}</span>
      </span>
    );
    const chev = <span className="tr-echev"><ChevronRight /></span>;
    // First-level approval progress (manager + approval-group slots). Rendered only
    // where the server sends the counts — an older API build omits them and the row
    // shows nothing rather than a misleading "0 of 0". Lives inside the flags cell so
    // the row grid is untouched.
    const slots = <>{slotCountChip(e)}{superSlotChip(e)}</>;
    const drift = canSpectrumExport ? driftByEmployee.get(e.employeeID) : undefined;
    const driftChip = drift && (
      <span className="ss-chip tr-drift" title={driftTooltip(drift)}>
        <AlertTriangle />Changed since Spectrum push
      </span>
    );
    const sageChip = sageActive
      ? sageExportChip(e.employeeID)
      : spectrumActive && !drift ? spectrumExportChip(e) : null;
    if (identityOnly) {
      return (
        <button key={e.employeeID} className="tr-erow is-identity" onClick={() => handleRowClick(e)} type="button">
          {avatar}{who}
          <span className="tr-eflags">{slots}{driftChip}{sageChip}{wcdpSlotOn && wcdpExportChip(e.employeeID)}{flagsCell(e)}{inactive && <InactiveBadge />}</span>
          {chev}
        </button>
      );
    }
    const state = !e.summary ? ' is-blank' : e.snapshotStale ? ' is-stale' : '';
    return (
      <button
        key={e.employeeID}
        className={`tr-erow${state}`}
        onClick={() => handleRowClick(e)}
        title={!e.summary ? 'No summary snapshot exists for this timesheet' : undefined}
        type="button"
      >
        {avatar}{who}
        <span className="ss-cell tr-ehours">{hoursCell(e)}</span>
        <span className="ss-cell tr-emoney">{moneyCell(e)}</span>
        <span className="tr-eflags">
          {slots}
          {e.snapshotStale && (
            <span className="ss-stalemark" title="Recompute pending — these numbers were stamped under a superseded rule set">
              <RefreshCw />
            </span>
          )}
          {driftChip}
          {sageChip}
          {wcdpSlotOn && wcdpExportChip(e.employeeID)}
          {flagsCell(e)}
        </span>
        {chev}
      </button>
    );
  };

  // ── RENDER: board ───────────────────────────────────────────────────────────
  const renderBoard = () => (
    <>
      {/* Pipeline board */}
      <div className="tr-flow">
        {BUCKETS.map((b) => {
          const n = empCount(b.k);
          const on = b.k === selectedBucket;
          const Icon = b.icon;
          const sups = supCount(b.k);
          return (
            <button
              key={b.k}
              className={`tr-stage ${b.tone}${on ? ' on' : ''}${!bucketsLoading && n === 0 ? ' zero' : ''}`}
              onClick={() => setSelectedBucket(b.k)}
              type="button"
            >
              <span className="fs-top"><Icon />{b.label}</span>
              {bucketsLoading ? (
                <span className="fs-count-spin" aria-label="Loading" role="status" />
              ) : (
                <span className="fs-count">{n}</span>
              )}
              {/* Group count in the stage's own vocabulary — "6 approvers" in the 1st
                  Approval stage, "10 supervisors" everywhere else. */}
              <span className="fs-sub">
                {bucketsLoading ? 'loading…' : n === 0 ? 'no timesheets' : `${sups} ${b.groupWord}${sups === 1 ? '' : 's'}`}
              </span>
              {b.k === 'payrollApproved' && !bucketsLoading && n > 0 && exportTile && (
                <span className="fs-sage" title={`${exportTile.erp} export status for this week`}>
                  <span className="ok"><b>{exportTile.exported}</b> exported</span>
                  <span className={exportTile.notExported > 0 ? 'todo' : undefined}>
                    &middot; <b>{exportTile.notExported}</b> not exported
                  </span>
                </span>
              )}
              <span className="fs-arrow"><ArrowRight /></span>
            </button>
          );
        })}
      </div>

      {/* Filter bar — Location / Supervisor narrow the base set (counts + list +
          queue); the text filter narrows only the visible list + queue. When the
          flag facet renders it joins on below, so the bar drops its bottom radius. */}
      <div className={`tr-filterbar${showFlagBar ? ' joined' : ''}`}>
        <span className="tr-fbtitle"><Filter />Filter this list</span>
        <FloatMenuButton
          label="Location"
          valueLabel={locationFilter === 'all'
            ? 'All locations'
            : (selectableLocations.find((l) => l.locationID === locationFilter)?.name ?? 'All locations')}
          isPlaceholder={locationFilter === 'all'}
          isActive={locationFilter !== 'all'}
          options={[
            { value: '', label: 'All locations' },
            ...selectableLocations.map((l) => ({ value: String(l.locationID), label: l.name })),
          ]}
          selectedValue={locationFilter === 'all' ? '' : String(locationFilter)}
          onSelect={(v) => handleLocationChange(v === '' ? 'all' : Number(v))}
        />
        {/* The label follows the stage's grouping key: the 1st Approval stage is grouped
            by approver (manager or approval group), every other stage by supervisor. */}
        <FloatMenuButton
          label={groupByApprover ? 'Approver' : selMeta.groupWord === 'required super' ? 'Required super' : 'Super'}
          valueLabel={(() => {
            if (supervisorFilter === 'all') return allGroupsLabel;
            const s = supervisorOptions.find((o) => o.id === supervisorFilter);
            return s ? supervisorOptionLabel(s.name, s.altEmployeeID) : allGroupsLabel;
          })()}
          isPlaceholder={supervisorFilter === 'all'}
          isActive={supervisorFilter !== 'all'}
          options={[
            { value: '', label: allGroupsLabel, count: empCount(selectedBucket) },
            ...supervisorOptions.map((s) => ({
              value: String(s.id),
              label: supervisorOptionLabel(s.name, s.altEmployeeID),
              count: s.count,
            })),
          ]}
          selectedValue={supervisorFilter === 'all' ? '' : String(supervisorFilter)}
          onSelect={(v) => setSupervisorFilter(v === '' ? 'all' : Number(v))}
          searchable
        />
        <div className="tr-inlinefilter">
          <Search />
          <input
            type="text"
            value={textFilter}
            placeholder="Filter by name or ID…"
            onChange={(e) => setTextFilter(e.target.value)}
          />
        </div>
        <button className="tr-btn sm tr-clear" onClick={clearFilters} type="button"><X />Clear</button>
        <span className="tr-fbgrow" />
        <button
          className="tr-btn primary tr-reviewall"
          disabled={listTotal === 0}
          onClick={reviewFiltered}
          type="button"
        >
          <ClipboardCheck />Review {listTotal} one-by-one
        </button>
      </div>

      {/* Flag facet — the row's chip vocabulary, made clickable in the filter bar
          instead of inside the row's <button>. ANY-of; a zero-count flag renders
          DISABLED rather than hidden so the facet's shape is stable week to week. */}
      {showFlagBar && (
        <div className="ss-flagbar">
          <span className="tr-fbtitle"><Flag />Flags</span>
          <span className="ss-fchips">
            {facetDefs.map((f) => {
              const n = facetCounts[f.k] ?? 0;
              const on = flagFilter.has(f.k);
              return (
                <button
                  key={f.k}
                  type="button"
                  className={`ss-fchip ${f.cls}${on ? ' on' : ''}`}
                  disabled={n === 0 && !on}
                  aria-pressed={on}
                  title={n === 0
                    ? `No timesheets in ${selMeta.label} carry ${f.label.toLowerCase()}`
                    : `${on ? 'Stop showing' : 'Show'} only timesheets with ${f.label.toLowerCase()}`}
                  onClick={() => toggleFlag(f.k)}
                >
                  {f.label}<b>{n}</b>
                </button>
              );
            })}
          </span>
          {flagsActive && (
            <button className="tr-btn sm tr-clear ss-fclear" onClick={clearFlags} type="button">
              <X />Clear flags
            </button>
          )}
        </div>
      )}

      {/* List / empty */}
      {bucketsLoading ? (
        <div className="tr-listnote">Loading timesheet data…</div>
      ) : listGroups.length === 0 ? (
        filtersActive ? (
          <div className="tr-empty">
            <div className="eic"><Search /></div>
            <div className="et">{flagsActive ? 'No timesheets carry those flags' : 'No timesheets match your filter'}</div>
            <p>{flagsActive
              ? <>Nothing in {selMeta.label} for Week {week} &middot; {describeScope()} matches the selected flags.</>
              : <>Nothing in {selMeta.label} for Week {week} &middot; {describeScope()}. Try widening the filter.</>}</p>
            <div className="tr-empty-btns">
              {flagsActive && (
                <button className="tr-btn sm" onClick={clearFlags} type="button"><X /> Clear flags</button>
              )}
              <button className="tr-btn sm" onClick={clearFilters} type="button"><X /> Clear filters</button>
            </div>
          </div>
        ) : (
          <div className="tr-empty">
            <div className="eic"><Inbox /></div>
            <div className="et">Nothing in {selMeta.label}{locNote} for Week {week}</div>
            <p>{BUCKETS.some((x) => x.k !== selectedBucket && empCount(x.k) > 0)
              ? 'This week has work in other stages:'
              : 'This stage is clear for the selected week.'}</p>
            <div className="tr-empty-btns">
              {BUCKETS.filter((x) => x.k !== selectedBucket && empCount(x.k) > 0).map((x) => (
                <button key={x.k} className="tr-btn sm" onClick={() => setSelectedBucket(x.k)} type="button">
                  {x.label} has <b className="mono">{empCount(x.k)}</b>
                </button>
              ))}
            </div>
          </div>
        )
      ) : (
        <div className="tr-list">
          <div className="tr-listnote">
            <Users />
            {/* An approver's list is counted in TIMESHEETS (deduped); a supervisor's crew
                is one row per employee, so it keeps the shipped wording. */}
            <b>{listTotal}</b> {groupByApprover
              ? (listTotal === 1 ? 'timesheet' : 'timesheets')
              : (listTotal === 1 ? 'employee' : 'employees')} &middot; <b>{listGroups.length}</b> {selMeta.groupWord}{listGroups.length === 1 ? '' : 's'} &middot; {selMeta.label} &middot; Week {week} &middot; {describeScope()}
            {/* One employee under several approvers is several signatures, not several
                timesheets — say so rather than letting the row count read as a total. */}
            {multiApproverEmployees > 0 && (
              <span className="tr-dupnote">
                <AlertCircle />
                {`${multiApproverEmployees} ${multiApproverEmployees === 1 ? 'employee appears' : 'employees appear'} under more than one approver — each approver must act`}
              </span>
            )}
            {heldOutCount > 0 && (
              <span
                className="ss-skipped"
                title="A missing or stale snapshot cannot answer a flag question, so those rows are held out while a flag filter is active"
              >
                {heldOutCount} without a usable snapshot held out
              </span>
            )}
          </div>

          {/* Bucket notes — one quiet explanation at the top instead of a field of
              dashes explaining itself once per row. The stale note is neutral grey,
              never amber: after a SNAPSHOT_VERSION bump every row carries it. */}
          {!identityOnly && staleCount > 0 && (
            <div className="ss-note">
              <RefreshCw />
              <span><b>Summaries are being recomputed.</b> Timesheet totals return as the backfill re-stamps this pay period. Approval is unaffected — open a timesheet for live numbers.</span>
            </div>
          )}

          {/* Collapse tools — only with multiple crews and no active text filter.
              Mirrors the drill-down console: Expand all disables once everything is
              expanded, Collapse all once everything is collapsed; both enabled only
              in the partially-expanded state. */}
          {collapsible && (() => {
            const expandedCount = listGroups.filter((g) => expanded.has(g.employeeID)).length;
            const allExpanded = listGroups.length > 0 && expandedCount === listGroups.length;
            const allCollapsed = expandedCount === 0;
            return (
              <div className="tr-collapsetools">
                <span className="tr-cthint">
                  <Users />
                  {`${listGroups.length} ${groupByApprover ? 'approver queues' : 'crews'} · click ${groupByApprover ? 'an approver' : 'a supervisor'} to expand`}
                </span>
                <button
                  className="tr-btn sm"
                  disabled={allExpanded}
                  onClick={() => setExpanded((prev) => {
                    const next = new Set(prev);
                    listGroups.forEach((g) => next.add(g.employeeID));
                    return next;
                  })}
                  type="button"
                >
                  <ChevronDown />Expand all
                </button>
                <button className="tr-btn sm" disabled={allCollapsed} onClick={() => setExpanded(new Set())} type="button">
                  <ChevronRight />Collapse all
                </button>
              </div>
            );
          })()}

          {/* Remind all unsubmitted — the pay-period-wide push notification, the one
              reminder the API offers. Scope is the whole bucket, not the filtered list. */}
          {selectedBucket === 'unsubmitted' && canRemindUnsubmitted && (
            <div className="tr-exportbar tr-remindbar">
              <span className="tr-exportlab">
                <Bell /> Unsubmitted &middot; Week {week}
              </span>
              <span className="tr-exportgrow" />
              <button
                className="tr-btn primary"
                onClick={handleRemindUnsubmitted}
                disabled={reminderSending || reminderSent || unsubmittedTotal === 0}
                title={reminderSent
                  ? 'A reminder has already been sent for this pay period'
                  : 'Send one push notification to every employee who has not submitted this week'}
                type="button"
              >
                <Bell /> {reminderSending
                  ? 'Sending…'
                  : reminderSent
                    ? 'Reminder sent'
                    : `Remind all ${unsubmittedTotal} unsubmitted`}
              </button>
            </div>
          )}

          {/* Sage export bar — Payroll Approved only; both buttons open the employee-selection
              picker scoped to the crews currently in view. Approval Access (§15.3.15): on top of
              showSageExport, require ExportPayroll OR ExportAccounting (admin implies both). */}
          {selectedBucket === 'payrollApproved' && canSage && (
            <div className="tr-exportbar">
              <span className="tr-exportlab">
                <Package /> {sageReconLoading && sageReconJob?.currentStep
                  ? sageReconJob.currentStep
                  : <>Sage export &middot; Week {week}{locNote}</>}
              </span>
              {/* TAS-180 roll-up over the approved set in view — the Sage filter never moves it. */}
              {sageSummary && (
                <span className="tr-sagesum">
                  <span><b>{sageSummary.approved}</b> approved</span>
                  <span>&middot; <b>{sageSummary.exported}</b> exported</span>
                  <span className={sageSummary.toExport > 0 ? 'todo' : undefined}>
                    &middot; <b>{sageSummary.toExport}</b> to export
                  </span>
                  {sageSummary.changed > 0 && (
                    <span className="warn">&middot; <b>{sageSummary.changed}</b> changed</span>
                  )}
                  {sageSummary.unconfirmed > 0 && (
                    <span className="warn">&middot; <b>{sageSummary.unconfirmed}</b> unconfirmed</span>
                  )}
                  {sageSummary.gone.length > 0 && (
                    <span
                      className="warn help"
                      title={sageSummary.gone
                        .map((x) => `${x.name || `#${x.altEmployeeID}`} — ${sageExportFacts(x)}`)
                        .join('\n')}
                    >
                      &middot; <b>{sageSummary.gone.length}</b> exported no longer approved
                    </span>
                  )}
                </span>
              )}
              <span className="tr-exportgrow" />
              {sageActive && (
                <span className="vx-seg tr-sageseg" role="group" aria-label="Sage export filter">
                  {([
                    ['all', 'All'],
                    ['notExported', 'Not exported'],
                    ['exported', 'Exported'],
                  ] as [SageExportFilter, string][]).map(([k, label]) => (
                    <button
                      key={k}
                      className={sageFilter === k ? 'on' : undefined}
                      aria-pressed={sageFilter === k}
                      onClick={() => setSageFilter(k)}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </span>
              )}
              <button
                className="tr-btn"
                onClick={handleSageExcel}
                disabled={sageExcelLoading || sageReconLoading || sageItems.length === 0 || !sageStatusReady}
                title={sageStatusReady ? undefined : SAGE_STATUS_LOADING_TITLE}
                type="button"
              >
                <FileSpreadsheet /> {sageExcelLoading ? 'Generating…' : 'Generate Sage Export Excel'}
              </button>
              {/* Deliberately not gated on sageItems.length — an already-exported week is exactly
                  what payroll wants to reconcile. */}
              <button className="tr-btn" onClick={handleSageReconcile} disabled={sageExcelLoading || sagePushLoading || sageReconLoading} type="button">
                <FileSpreadsheet /> {sageReconLoading ? `Reconciling… ${sageReconJob?.progress ?? 0}%` : 'Reconcile with Sage'}
              </button>
              <button
                className="tr-btn primary"
                onClick={handleSagePush}
                disabled={sagePushLoading || sageReconLoading || sageItems.length === 0 || !sageStatusReady}
                title={sageStatusReady ? undefined : SAGE_STATUS_LOADING_TITLE}
                type="button"
              >
                <UploadCloud /> Export to Sage
              </button>
            </div>
          )}

          {/* NPM payroll bar — Payroll Approved only; Print All (printAllTimesheets config +
              admin/payroll bucket, honors the location filter) and the Payroll Approved
              Summary xlsx (showPayrollApprovedSummaryReport + exportPayroll, pay-period-wide)
              share one bar. Both are job-based; the label shows the running job's step. */}
          {selectedBucket === 'payrollApproved' && (canPrintAll || canDownloadSummary || canDownloadPostVerification || canWallcraftSummary || spectrumActive || canWcdpExport) && (
            <div className="tr-exportbar">
              <span className="tr-exportlab">
                <Package /> {printAllLoading && printAllJob?.currentStep
                  ? printAllJob.currentStep
                  : summaryLoading && summaryJob?.currentStep
                    ? summaryJob.currentStep
                    : verifyLoading && verifyJob?.currentStep
                      ? verifyJob.currentStep
                      : <>Payroll approved &middot; Week {week}{locNote}</>}
              </span>
              {/* Wallcraft: Spectrum push roll-up over the approved set in view — the filter never moves it. */}
              {spectrumActive && spectrumSummary && (
                <span className="tr-sagesum">
                  <span><b>{spectrumSummary.approved}</b> approved</span>
                  <span>&middot; <b>{spectrumSummary.exported}</b> exported</span>
                  <span className={spectrumSummary.toExport > 0 ? 'todo' : undefined}>
                    &middot; <b>{spectrumSummary.toExport}</b> to export
                  </span>
                  {spectrumSummary.changed > 0 && (
                    <span className="warn">&middot; <b>{spectrumSummary.changed}</b> changed</span>
                  )}
                  {spectrumSummary.gone.length > 0 && (
                    <span
                      className="warn help"
                      title={spectrumSummary.gone
                        .map((x) => `${x.name || `#${x.altEmployeeID}`} — ${spectrumExportFacts(x)}`)
                        .join('\n')}
                    >
                      &middot; <b>{spectrumSummary.gone.length}</b> exported no longer approved
                    </span>
                  )}
                </span>
              )}
              {/* WCDP: batch-export roll-up over the approved set in view — the filter never moves it. */}
              {wcdpSummary && (
                <span className="tr-sagesum">
                  <span><b>{wcdpSummary.approved}</b> approved</span>
                  <span>&middot; <b>{wcdpSummary.exported}</b> exported</span>
                  <span className={wcdpSummary.toExport > 0 ? 'todo' : undefined}>
                    &middot; <b>{wcdpSummary.toExport}</b> to export
                  </span>
                  {wcdpSummary.changed > 0 && (
                    <span className="warn">&middot; <b>{wcdpSummary.changed}</b> changed</span>
                  )}
                  {wcdpSummary.unconfirmed > 0 && (
                    <span className="warn">&middot; <b>{wcdpSummary.unconfirmed}</b> unconfirmed</span>
                  )}
                  {wcdpSummary.gone > 0 && (
                    <span className="warn">&middot; <b>{wcdpSummary.gone}</b> exported no longer approved</span>
                  )}
                </span>
              )}
              <span className="tr-exportgrow" />
              {/* WCDP: shown as soon as the bucket is (disabled until the export status loads) so
                  nothing on the bar moves when it arrives. */}
              {(spectrumActive || wcdpSlotOn) && (
                <span className="vx-seg tr-sageseg" role="group" aria-label={spectrumActive ? 'Spectrum export filter' : 'Batch export filter'}>
                  {([
                    ['all', 'All'],
                    ['notExported', 'Not exported'],
                    ['exported', 'Exported'],
                  ] as [SageExportFilter, string][]).map(([k, label]) => (
                    <button
                      key={k}
                      className={sageFilter === k ? 'on' : undefined}
                      aria-pressed={sageFilter === k}
                      onClick={() => setSageFilter(k)}
                      disabled={!spectrumActive && !wcdpActive}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </span>
              )}
              {canPrintAll && (
                <button className="tr-btn" onClick={handlePrintAll} disabled={printAllLoading} type="button">
                  <Printer /> {printAllLoading ? `Preparing… ${printAllJob?.progress ?? 0}%` : 'Print All Timesheets'}
                </button>
              )}
              {/* Not `primary` — the payroll summary stays the primary action on this bar. */}
              {canDownloadPostVerification && (
                <button className="tr-btn" onClick={handleVistaPostVerification} disabled={verifyLoading} type="button">
                  <ShieldCheck /> {verifyLoading ? `Verifying… ${verifyJob?.progress ?? 0}%` : 'Vista Post Verification'}
                </button>
              )}
              {canDownloadSummary && (
                <button className="tr-btn primary" onClick={handlePayrollSummary} disabled={summaryLoading} type="button">
                  <FileSpreadsheet /> {summaryLoading ? `Generating… ${summaryJob?.progress ?? 0}%` : 'Download Payroll Summary Report'}
                </button>
              )}
              {/* Wallcraft: the whole week (every crew), built from what was pushed to Spectrum. */}
              {canWallcraftSummary && (
                <>
                  <button
                    className="tr-btn primary"
                    onClick={() => { void handleWallcraftDownload('summary'); }}
                    disabled={wcDownloading !== null}
                    title="One row per crew, as exported to Spectrum"
                    type="button"
                  >
                    <FileSpreadsheet /> {wcDownloading === 'summary' ? 'Generating…' : 'Download Weekly Summary'}
                  </button>
                  <button
                    className="tr-btn primary"
                    onClick={() => { void handleWallcraftDownload('detail'); }}
                    disabled={wcDownloading !== null}
                    title="One tab per crew: employee totals and line detail, as exported to Spectrum"
                    type="button"
                  >
                    <FileSpreadsheet /> {wcDownloading === 'detail' ? 'Generating…' : 'Download Payroll Detail'}
                  </button>
                </>
              )}
              {/* WCDP (TAS-682): the whole week's Paycor file, IAS-exported employees only.
                  Fixed width — the spinner replaces the icon, the label never changes. */}
              {canWcdpExport && (
                <button
                  className="tr-btn primary tr-paycorbtn"
                  onClick={() => { void handleWcdpPaycor(); }}
                  disabled={paycorBusy !== null || !currentPayPeriodID}
                  type="button"
                >
                  {paycorBusy ? <span className="vx-spin sm wx-spin-ink" /> : <FileSpreadsheet />} Generate Paycor File
                </button>
              )}
            </div>
          )}

          {/* Approve All bar — Payroll Pending only (the v1 Approve All, ported). Scope is the
              crews currently in view, so the active filters narrow it; the counts say so.
              Gated on PayrollApproveTimesheets, the permission the API enforces. */}
          {canBulkApprove && listGroups.length > 0 && (
            <div className="tr-exportbar tr-approvebar">
              <span className="tr-exportlab">
                <CheckCircle2 /> {bulkRunning && bulkCrewId === null && bulkProgress
                  ? `Approving… ${bulkProgress.done} of ${bulkProgress.total}`
                  : (
                    <>
                      Payroll approve &middot; Week {week}{locNote}
                      {bulkPartition.blocked.length > 0 && (
                        <span className="tr-bulkskiplab">
                          &nbsp;&middot; {bulkPartition.blocked.length} skipped &middot; sick rate
                        </span>
                      )}
                    </>
                  )}
              </span>
              <span className="tr-exportgrow" />
              {bulkRunning && bulkCrewId === null ? (
                <button className="tr-btn" onClick={cancelBulkApprove} type="button">
                  <X /> Stop
                </button>
              ) : (
                <button
                  className="tr-btn primary"
                  onClick={() => startBulkApprove(
                    listGroups.flatMap((g) => g.employees ?? []),
                    filtersActive ? describeScope() : 'All supervisors',
                    null
                  )}
                  disabled={bulkRunning || bulkPartition.ready.length === 0}
                  title="Payroll approve every timesheet currently in view"
                  type="button"
                >
                  <CheckCircle2 /> Approve All &middot; {bulkPartition.ready.length} ready
                </button>
              )}
            </div>
          )}

          {sageFilterOn && sageVisibleGroups.length === 0 && (
            <div className="ss-note">
              <Filter />
              <span>
                <b>No {sageFilter === 'exported' ? 'exported' : 'unexported'} employees in view.</b>{' '}
                <button className="tr-btn sm" onClick={() => setSageFilter('all')} type="button">Show all</button>
              </span>
            </div>
          )}

          {sageVisibleGroups.map(({ g, rows }) => {
            const exp = isExpanded(g.employeeID);
            return (
              <div key={g.employeeID} className={`tr-group${exp ? ' open' : ''}`}>
                <div
                  className={`tr-group-head${collapsible ? ' clk' : ''}`}
                  onClick={collapsible ? () => setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(g.employeeID)) next.delete(g.employeeID); else next.add(g.employeeID);
                    return next;
                  }) : undefined}
                >
                  {collapsible && <span className="tr-gchev"><ChevronRight /></span>}
                  <span className="tr-sav">{groupInitials(g)}</span>
                  {/* Name + location + crew roll-up become one flex-column child so the
                      roll-up sits under the name. The button cluster stays a direct
                      child of the header — its margin-left:auto anchor is unchanged. */}
                  <span className="ss-sup">
                    <span className="ss-supline">
                      <span className="tr-sname">{groupLabel(g)}</span>
                      {/* An approval group is a set of people, not one of them — the tag
                          says so, and the group carries no single location to show. */}
                      {g.kind === 'approvalGroup' ? (
                        <span className="tr-kindtag group"><Users />Approval group</span>
                      ) : (
                        <span className="tr-sloc">{locationName(g.locationID)}</span>
                      )}
                    </span>
                    {rollupLine(g)}
                  </span>
                  <span className="tr-scount"><span className="pill neutral">{g.employees?.length ?? g.count ?? 0} emp</span></span>
                  <button
                    className="tr-btn sm primary tr-crewbtn"
                    onClick={(e) => { e.stopPropagation(); reviewCrew(g); }}
                    title={groupByApprover
                      ? 'Review this approver’s queue one-by-one'
                      : 'Review this crew one-by-one'}
                    type="button"
                  >
                    <ClipboardCheck /> Review {groupByApprover ? 'queue' : 'crew'}
                  </button>
                  {/* The v1 Approve All's exact scope: one supervisor's list. stopPropagation
                      is required — the header itself is the collapse toggle. */}
                  {canBulkApprove && (
                    bulkRunning && bulkCrewId === g.employeeID ? (
                      <button
                        className="tr-btn sm tr-approvecrew"
                        onClick={(e) => { e.stopPropagation(); cancelBulkApprove(); }}
                        type="button"
                      >
                        <X /> Stop{bulkProgress ? ` ${bulkProgress.done}/${bulkProgress.total}` : ''}
                      </button>
                    ) : (
                      <button
                        className="tr-btn sm tr-approvecrew"
                        onClick={(e) => {
                          e.stopPropagation();
                          startBulkApprove(g.employees ?? [], `${g.name.last}, ${g.name.first}’s crew`, g.employeeID);
                        }}
                        disabled={bulkRunning}
                        title="Payroll approve this crew"
                        type="button"
                      >
                        <CheckCircle2 /> Approve crew
                      </button>
                    )
                  )}
                  {/* Print batches a SUPERVISOR's crew; an approver group is neither a
                      supervisor nor a crew, so the 1st Approval stage has no batch to print. */}
                  {selectedBucket !== 'unsubmitted' && !groupByApprover && (
                    <button
                      className="tr-btn sm tr-print"
                      onClick={(e) => { e.stopPropagation(); handlePrintSupervisor(g); }}
                      disabled={printingSupId === g.employeeID}
                      type="button"
                    >
                      <Printer /> {printingSupId === g.employeeID ? 'Preparing…' : 'Print'}
                    </button>
                  )}
                  {/* Spectrum pre-time cards, one crew at a time — like the Vista button
                      below it, the API takes a single supervisorID. */}
                  {selectedBucket === 'payrollApproved' && canSpectrumExport && !groupByApprover && (
                    <button
                      className="tr-btn sm tr-spectrum"
                      onClick={(e) => { e.stopPropagation(); setSpectrumCrew(g); }}
                      title="Preview, download or push this crew's Spectrum pre-time cards"
                      type="button"
                    >
                      <UploadCloud /> Spectrum
                    </button>
                  )}
                  {/* WCDP IAS export, one crew at a time (TAS-682) — the API takes one supervisorID. */}
                  {selectedBucket === 'payrollApproved' && canWcdpExport && !groupByApprover && (
                    <button
                      className="tr-btn sm tr-wcdp"
                      onClick={(e) => { e.stopPropagation(); setWcdpCrew(g); }}
                      type="button"
                    >
                      <UploadCloud /> Batch Export
                    </button>
                  )}
                  {/* Crew "x of y exported". The slot is held (invisible) until the record loads,
                      and has a fixed min-width, so the buttons beside it never move. */}
                  {wcdpSlotOn && !groupByApprover && (() => {
                    if (!(g.employees ?? []).length) return null;
                    if (!wcdpActive) return <span className="ss-chip tr-driftcount tr-wcdpcount is-pending" aria-hidden="true" />;
                    const { exported: done, total } = wcdpCrewExportCount(g.employees ?? [], wcdpExportedByEmp!);
                    return (
                      <span className={`ss-chip tr-driftcount tr-wcdpcount ${done === total ? 'tr-sage-ok' : 'tr-sage-off'}`}>
                        {done} of {total} exported
                      </span>
                    );
                  })()}
                  {selectedBucket === 'payrollApproved' && canSpectrumExport && !groupByApprover
                    && (driftBySupervisor.get(g.employeeID)?.length ?? 0) > 0 && (
                    <span
                      className="ss-chip tr-drift tr-driftcount"
                      title={(driftBySupervisor.get(g.employeeID) ?? [])
                        .map((d) => `${d.name} #${d.altEmployeeID} — ${formatDriftTotalsShift(d)}`
                          + (d.stillApproved ? '' : ' · No longer Payroll Approved'))
                        .join('\n')}
                    >
                      <AlertTriangle />{driftBySupervisor.get(g.employeeID)?.length} changed since push
                    </span>
                  )}
                  {/* Crew roll-up of the row chips: pushed of pushable (nothing-to-export excluded). */}
                  {spectrumActive && !groupByApprover && (() => {
                    const crew = g.employees ?? [];
                    const pushable = crew.filter((e) => spectrumExportedByEmp!.has(e.employeeID) || !spectrumNothingToExport(e));
                    if (!pushable.length) return null;
                    const pushed = pushable.filter((e) => spectrumExportedByEmp!.has(e.employeeID)).length;
                    return (
                      <span
                        className={`ss-chip tr-driftcount ${pushed === pushable.length ? 'tr-sage-ok' : 'tr-sage-off'}`}
                        title={`${pushed} of ${pushable.length} pushed to Spectrum this week`}
                      >
                        {pushed} of {pushable.length} pushed
                      </span>
                    );
                  })()}
                  {/* Vista push, one crew at a time — the API takes a single supervisorID,
                      so there is no bucket-level equivalent to put on the export bar. */}
                  {selectedBucket === 'payrollApproved' && canVistaExport && !groupByApprover && (
                    <button
                      className="tr-btn sm tr-vista"
                      onClick={(e) => { e.stopPropagation(); setVistaCrew(g); }}
                      title="Push this crew's payroll-approved hours and pieces to Vista"
                      type="button"
                    >
                      <UploadCloud /> Vista Export
                    </button>
                  )}
                </div>
                {exp && rows.map((e) => employeeRow(e))}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ── RENDER: queue chrome + console ──────────────────────────────────────────
  const renderQueue = () => {
    if (!origin || !originMeta) return null;

    // Completion state
    if (isComplete) {
      const nextKey = NEXT_BUCKET[origin.bucket];
      return (
        <>
          <button className="tr-back" onClick={backToBoard} type="button">
            <ChevronLeft />Back to {originMeta.label} — Week {week}
          </button>
          <div className="tr-empty">
            <div className="eic done"><CheckCircle2 /></div>
            <div className="et">{originMeta.label} cleared for Week {week}</div>
            <p>
              You cleared all {total} timesheet{total === 1 ? '' : 's'}.
              {nextKey ? <> They moved to <b>{BMAP[nextKey].label}</b>.</> : null}
            </p>
            <div className="tr-empty-btns">
              <button className="tr-btn sm" onClick={backToBoard} type="button"><ClipboardCheck /> Back to the board</button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <button className="tr-back" onClick={backToBoard} type="button">
          <ChevronLeft />Back to {originMeta.label} — Week {week}{canApproveOrigin ? ` (${remaining} left)` : ''}
        </button>

        {/* Queue bar — Prev/Next + "Reviewing X of N" text (no per-item dots) */}
        <div className="tr-qbar">
          <div className="tr-qnav">
            <button
              className="tr-btn sm"
              disabled={qi <= 0}
              onClick={() => setQi((i) => Math.max(0, i - 1))}
              type="button"
            >
              <ChevronLeft />Prev
            </button>
            <button
              className="tr-btn sm"
              disabled={qi >= total - 1}
              onClick={() => setQi((i) => Math.min(total - 1, i + 1))}
              type="button"
            >
              Next<ChevronRight />
            </button>
          </div>
          <span className="tr-qpos">
            Reviewing <b>{qi + 1}</b> of <b>{total}</b> &middot; <span className="tr-qbk">{originMeta.label}</span>
            {origin.scope ? <> &middot; <span className="tr-qscope">{origin.scope}</span></> : null}
          </span>
        </div>

        {/* Breadcrumb */}
        {currentEntry && (
          <div className="tr-crumb">
            <span className="tr-crumb-link" onClick={backToBoard}>Timesheet Review</span>
            <ChevronRight />
            <span className="tr-crumb-link" onClick={backToBoard}>{currentEntry.supervisorName || '—'}</span>
            <ChevronRight />
            <b>{displayName(currentEntry.name)}</b>
          </div>
        )}

        {/* Sick-rate panel — payroll-pending timesheets with sick claims (rate UI enabled).
            Standalone rate entry (discoverability): payroll can save the rate here without
            touching Approve; Save & Approve chains straight into the payroll approval. */}
        {timesheetData && showSickRatePanel && (
          <div className={`tr-sickbar${hasUnratedSick ? ' needed' : ''}`}>
            <span className="tr-sicklab">
              <AlertCircle />
              {hasUnratedSick ? 'Sick rate needed' : 'Sick rate saved'}
            </span>
            <span className="tr-sicksum">
              {formatDecimalHours(totalSickHours)} sick hour{totalSickHours === 1 ? '' : 's'} this week
              {savedSickRate != null
                ? <> &middot; current rate <b>${savedSickRate.toFixed(2)}/hr</b></>
                : <> &middot; enter the hourly rate before payroll approval</>}
            </span>
            <span className="tr-sickgrow" />
            <input
              type="number"
              className="tr-sickrate"
              placeholder="Hourly rate (e.g. 18.50)"
              value={sickPanelRate}
              onChange={(e) => setSickPanelRate(e.target.value)}
              min="0"
              step="0.01"
            />
            <button
              className="tr-btn"
              onClick={() => handleSaveSickRate(false)}
              disabled={sickRateSaving || approveLoading}
              type="button"
            >
              {sickRateSaving ? 'Saving…' : 'Save Rate'}
            </button>
            {canApproveOrigin && (
              <button
                className="tr-btn primary"
                onClick={() => handleSaveSickRate(true)}
                disabled={sickRateSaving || approveLoading}
                type="button"
              >
                {approveLoading ? 'Approving…' : 'Save & Approve'}
              </button>
            )}
          </div>
        )}

        {/* Console */}
        {reviewLoading && !timesheetData && <div className="tr-listnote">Loading timesheet…</div>}
        {reviewError && <div className="tr-error">{reviewError}</div>}
        {timesheetData && (
          <EmployeeTimesheetReviewConsole
            timesheetData={timesheetData}
            driveTimeEntries={driveTimeEntries}
            timeOffRequests={timeOffRequests}
            timeOffDetailMap={timeOffDetailMap}
            holidays={holidays}
            selectedStatus={actualStatus ?? origin.bucket}
            payPeriods={payPeriods}
            reviewWeekIdx={reviewWeekIdx}
            onSelectWeek={onSelectWeek}
            supervisorName={currentEntry?.supervisorName}
            approveLoading={approveLoading}
            reviewLoading={reviewLoading}
            noTimesheetForWeek={noTimesheetForWeek}
            onApproveTimeOff={handleApproveTimeOff}
            onDenyTimeOff={handleDenyTimeOff}
            onSendBack={handleSendBack}
            onApproveWeek={handleApproveToPayroll}
            onRefresh={() => { if (currentEntry) loadReview(currentEntry, payPeriodID); }}
            canApproveStagePerm={canApproveStage(userRole, actualStatus ?? origin.bucket)}
            canUnlockStagePerm={canUnlockStage(userRole, actualStatus ?? origin.bucket)}
            // M1-B3: payroll drill-down actions + server preview numbers. The
            // callbacks are passed only when the feature flag AND the caller's
            // permission allow — the console renders nothing without them.
            payrollEntryCounts={payrollState?.entryCounts ?? null}
            payrollPreview={payrollPreview}
            payrollPreviewLoading={payrollPreviewLoading}
            onEditAsPayroll={payrollEditingOn && canEditEntries(userRole) ? handleEditAsPayroll : undefined}
            onSubmitForEmployee={payrollEditingOn && canSubmitForEmployee(userRole) && payrollState
              ? () => setSubmitWizardOpen(true)
              : undefined}
            // Change history is a READ affordance: gated like the API's history
            // reads (viewTimesheets-or-admin — `can` grants super admin), never on
            // EditTimesheetEntries. The console also requires the feature flag.
            canViewHistoryPerm={canEditEntries(userRole)}
            // Change Supervisor lives in the console's header action row alongside
            // Refresh / Change history / Print. Passing the callback IS the gate —
            // undefined when the caller may not reassign, so nothing renders.
            onChangeSupervisor={canChangeSupervisor ? openSupervisorModal : undefined}
            // Misc task cost-code editor (V1 parity): permission-gated, and off for
            // combined-week tenants where misc rows aren't shown per day.
            canEditMiscTaskCodePerm={
              can(userRole, 'editMiscTaskAccountCodeAndDescription') && !config.combineTasksInWeek
            }
            // NPM multi-tier review (design_handoff_npm_timesheet_review §3): the
            // console owns the per-slot approvals, but the board owns the queue and
            // the optimistic move — so it hands over the signed-in identity (the
            // value the API records verbatim, errata 7) and takes the advance back.
            adminEmployeeID={userID}
            adminName={userName}
            // TAS-679 (WCDP Modified Duty): the roles the API lets approve HR's slot on her
            // behalf. The console shows the button only on a 2+ slot payload.
            canApproveOnBehalfModifiedDuty={canApproveModifiedDutyOnBehalf(userRole)}
            onStageAdvanced={(fromStatus) => onApproveSuccess(fromStatus)}
            onToast={showToast}
          />
        )}

      </>
    );
  };

  return (
    <div className="trb" ref={trbRef}>
      {/* Page head */}
      <div className="page-head">
        <div className="ph-ic"><ClipboardCheck /></div>
        <div>
          <h1>Timesheet Review</h1>
          <div className="ph-sub">
            Timesheets flow left &rarr; right through the week. Pick a stage to review its supervisors and crew — the week in the header governs everything.
          </div>
        </div>
        {/* Board only — the review console has its own Refresh for the open timesheet. */}
        {view === 'board' && (
          <button
            type="button"
            className="ph-refresh"
            onClick={() => { loadAllBuckets(); }}
            disabled={bucketsLoading}
            title="Refresh the stage counts and crew lists"
          >
            <RefreshCw className={bucketsLoading ? 'spin' : undefined} />
            Refresh
          </button>
        )}
      </div>

      {view === 'board' ? renderBoard() : renderQueue()}

      {/* Sage export employee-selection modal (Excel + API push share it) */}
      {sageModalMode && currentPayPeriod && (
        <SageExportSelectModal
          mode={sageModalMode}
          items={sageItems}
          week={week}
          dateRange={formatPayPeriodRange(currentPayPeriod)}
          locationLabel={locationFilter !== 'all' ? locationName(locationFilter) : undefined}
          busy={sageModalMode === 'excel' ? sageExcelLoading : sagePushLoading}
          onConfirm={sageModalMode === 'excel' ? handleSageExcelConfirm : handleSagePushConfirm}
          onClose={() => { if (!sageExcelLoading && !sagePushLoading) setSageModalMode(null); }}
          /* API push only (TAS-180) — Excel mode records nothing and stays unchanged. */
          exported={sageModalMode === 'api' ? sageExportedByEmp ?? undefined : undefined}
        />
      )}

      {/* NPM Vista export for one crew. Blocking once the job starts — see the modal. */}
      {vistaCrew && currentPayPeriod && (
        <VistaExportModal
          supervisorID={vistaCrew.employeeID}
          supervisorName={`${vistaCrew.name.last}, ${vistaCrew.name.first}`}
          supervisorAltID={vistaCrew.altEmployeeID ?? String(vistaCrew.employeeID)}
          locationLabel={locationName(vistaCrew.locationID)}
          payPeriodID={currentPayPeriodID}
          week={week}
          year={Number(currentPayPeriod.AccountingYear)}
          dateRange={formatPayPeriodRange(currentPayPeriod)}
          onClose={() => setVistaCrew(null)}
          onToast={showToast}
        />
      )}

      {/* Wallcraft Spectrum pre-time-card export for one crew. */}
      {spectrumCrew && currentPayPeriod && (
        <SpectrumExportModal
          crew={spectrumCrew}
          payPeriodID={currentPayPeriodID}
          payPeriod={currentPayPeriod}
          onClose={() => setSpectrumCrew(null)}
          onPushed={handleSpectrumPushed}
        />
      )}

      {/* WCDP IAS export for one crew (TAS-682). The modal owns its result / failure panels. */}
      {wcdpCrew && !!currentPayPeriodID && (
        <WcdpIasExportModal
          crew={wcdpCrew}
          payPeriodID={currentPayPeriodID}
          week={week}
          exported={wcdpExportedByEmp ?? new Map()}
          onClose={() => setWcdpCrew(null)}
          onRecorded={() => setWcdpStatusNonce((n) => n + 1)}
        />
      )}

      {/* Sick-rate modal */}
      {sickRateModalOpen && (
        <div className="trb-modal-bg">
          <div className="trb-modal small" onClick={(e) => e.stopPropagation()}>
            <div className="trb-sick">
              <h3>Enter Sick Hourly Rate</h3>
              <p>This employee has sick time claims and piece work this week. Enter the hourly rate to apply before approving.</p>
              <input
                type="number"
                placeholder="Hourly rate (e.g. 18.50)"
                value={sickRateInput}
                onChange={(e) => setSickRateInput(e.target.value)}
                min="0"
                step="0.01"
              />
              <div className="trb-sick-actions">
                <button className="tr-btn" onClick={() => setSickRateModalOpen(false)} disabled={approveLoading} type="button">Cancel</button>
                <button className="tr-btn primary" onClick={handleSickRateConfirm} disabled={approveLoading} type="button">
                  {approveLoading ? 'Approving…' : 'Confirm & Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Supervisor picker. A searchable dropdown rather than a standing list:
          WCDP has ~66 active supervisors, and typing the name or the EE# is faster than
          scrolling. Picking only stages the choice — the confirm below is what writes. */}
      {supModalOpen && (
        <div className="trb-modal-bg" onClick={() => { if (!supSaving) setSupModalOpen(false); }}>
          <div className="trb-modal small" onClick={(e) => e.stopPropagation()}>
            <div className="trb-sup">
              <h3>Change Supervisor</h3>
              <p>
                <b>{currentEntry ? displayName(currentEntry.name) : ''}</b> &middot; Week {week} &middot;
                currently <b>{currentEntry?.supervisorName || '—'}</b>.
                This affects this timesheet only, not the employee’s regular supervisor.
              </p>
              {/* The menu portals to <body> at z-index 700, so it clears the modal;
                  option clicks bubble through the React tree into .trb-modal, whose
                  stopPropagation keeps the backdrop from closing underneath. */}
              <div className="trb-suplabel">New supervisor</div>
              <div className="trb-suppick">
                <FloatMenuButton
                  label="New supervisor"
                  valueLabel={
                    supPicked
                      ? supLabel(supPicked)
                      : supOptionsLoading ? 'Loading…' : 'Search name or EE#…'
                  }
                  isPlaceholder={!supPicked}
                  isActive={!!supPicked}
                  options={supMenuOptions}
                  selectedValue={supPick == null ? '' : String(supPick)}
                  onSelect={(v) => setSupPick(Number(v))}
                  disabled={supOptionsLoading || supMenuOptions.length === 0 || supSaving}
                  searchable
                />
              </div>
              {!supOptionsLoading && supMenuOptions.length === 0 && (
                <div className="trb-supnote">No other supervisors available.</div>
              )}
              <div className="trb-sick-actions">
                <button className="tr-btn" onClick={() => setSupModalOpen(false)} disabled={supSaving} type="button">
                  Cancel
                </button>
                <button
                  className="tr-btn primary"
                  onClick={() => { if (supPicked) confirmReassignSupervisor(supPicked); }}
                  disabled={!supPicked || supSaving}
                  type="button"
                >
                  {supSaving ? 'Saving…' : 'Change supervisor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payroll submit-for-employee wizard (M1-B3) — rendered only with the
          editable state loaded; every open is a fresh sitting (fresh answers,
          fresh editSessionID). */}
      {submitWizardOpen && payrollState && currentPayPeriod && (
        <PayrollSubmitWizard
          state={payrollState}
          payPeriodID={currentPayPeriodID}
          payPeriodLabel={formatPayPeriodRange(currentPayPeriod)}
          onClose={() => setSubmitWizardOpen(false)}
          onSuccess={handlePayrollSubmitSuccess}
          onOpenSubmittedWeek={handleConflictOpenSubmittedWeek}
          onBackToBoard={handleConflictBackToBoard}
        />
      )}

      {/* Confirm / result modal */}
      {cmConfig && <ConfirmModal {...cmConfig} onClose={dismissCM} />}

      {/* Transient action toast (print / Sage export feedback) */}
      {toast && <div className="trb-toast">{toast}</div>}
    </div>
  );
};

export default TimesheetReviewBoardPage;
